(() => {
  'use strict';

  const VERSION = '10.2.0-recovery';
  const instances = new WeakMap();
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => Array.from(root?.querySelectorAll(selector) || []);
  const randomItem = (items) => items[Math.floor(Math.random() * items.length)];
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

  const PRESETS = {
    midnight: {
      title: 'Midnight Courage', color: '#17142d', colorName: 'Midnight', energyWord: 'Steady',
      rhythm: 'heartbeat', instrument: 'bass', layerInstrument: 'cello', harmony: 'octave', layerLevel: 28,
      ambient: 'rain', tempo: 68, energy: 42, ambientLevel: 58, reverb: 36, echo: 18, space: 62, texture: 'deep',
      phrase: 'Quiet courage still counts.'
    },
    rocket: {
      title: 'Pink Rocket Brave', color: '#ff4fa3', colorName: 'Pink', energyWord: 'Playful',
      rhythm: 'bounce', instrument: 'drums', layerInstrument: 'chimes', harmony: 'fifth', layerLevel: 44,
      ambient: 'night', tempo: 118, energy: 86, ambientLevel: 36, reverb: 26, echo: 24, space: 78, texture: 'glassy',
      phrase: 'I can bring my own kind of brave.'
    },
    ocean: {
      title: 'Calm Ocean Flow', color: '#40c8ff', colorName: 'Aqua', energyWord: 'Quiet',
      rhythm: 'flow', instrument: 'harp', layerInstrument: 'flute', harmony: 'floating', layerLevel: 34,
      ambient: 'ocean', tempo: 72, energy: 46, ambientLevel: 66, reverb: 58, echo: 12, space: 84, texture: 'airy',
      phrase: 'I can move at the pace that fits me.'
    },
    golden: {
      title: 'Golden Confidence', color: '#ffd34e', colorName: 'Gold', energyWord: 'Powerful',
      rhythm: 'pulse', instrument: 'violin', layerInstrument: 'harp', harmony: 'third', layerLevel: 38,
      ambient: 'forest', tempo: 92, energy: 72, ambientLevel: 38, reverb: 44, echo: 8, space: 70, texture: 'cinematic',
      phrase: 'I can take up space and still be kind.'
    },
    'inner-light': {
      title: 'Lantern Glow', color: '#ffc94f', colorName: 'Lantern Gold', energyWord: 'Steady', rhythm: 'drift',
      instrument: 'harp', layerInstrument: 'glass-bells', harmony: 'floating', layerLevel: 32, ambient: 'night', tempo: 64,
      energy: 38, ambientLevel: 48, reverb: 55, echo: 16, space: 80, texture: 'glassy', phrase: 'My feelings can be noticed with care.'
    },
    curiosity: {
      title: 'Curiosity Lab', color: '#36dfff', colorName: 'Electric Aqua', energyWord: 'Curious', rhythm: 'orbit',
      instrument: 'synth', layerInstrument: 'marimba', harmony: 'fifth', layerLevel: 36, ambient: 'space', tempo: 102,
      energy: 68, ambientLevel: 34, reverb: 38, echo: 24, space: 86, texture: 'glassy', phrase: 'More than one possibility can exist.'
    },
    gratitude: {
      title: 'Golden Gratitude', color: '#ffd34e', colorName: 'Gold', energyWord: 'Steady', rhythm: 'flow',
      instrument: 'harp', layerInstrument: 'chimes', harmony: 'third', layerLevel: 26, ambient: 'forest', tempo: 68,
      energy: 40, ambientLevel: 46, reverb: 46, echo: 10, space: 68, texture: 'warm', phrase: 'I can notice one small thing that matters.'
    },
    outside: {
      title: 'Mountain Horizon', color: '#56d985', colorName: 'Trail Green', energyWord: 'Hopeful', rhythm: 'drift',
      instrument: 'guitar', layerInstrument: 'flute', harmony: 'fifth', layerLevel: 30, ambient: 'wind', tempo: 88,
      energy: 62, ambientLevel: 54, reverb: 35, echo: 8, space: 74, texture: 'airy', phrase: 'A new path can begin with one step.'
    },
    kindness: {
      title: 'Kindness Ripple', color: '#ff6fb5', colorName: 'Kindness Pink', energyWord: 'Steady', rhythm: 'flow',
      instrument: 'kalimba', layerInstrument: 'chimes', harmony: 'third', layerLevel: 34, ambient: 'rain', tempo: 76,
      energy: 48, ambientLevel: 40, reverb: 42, echo: 12, space: 66, texture: 'warm', phrase: 'Care can include boundaries, repair, and respect.'
    },
    goals: {
      title: 'Summit Pulse', color: '#7567ff', colorName: 'Summit Violet', energyWord: 'Powerful', rhythm: 'pulse',
      instrument: 'strings', layerInstrument: 'drums', harmony: 'octave', layerLevel: 28, ambient: 'wind', tempo: 96,
      energy: 72, ambientLevel: 30, reverb: 48, echo: 8, space: 76, texture: 'cinematic', phrase: 'One next step can still move me forward.'
    },
    stories: {
      title: 'Story Lantern', color: '#ff9d4f', colorName: 'Story Amber', energyWord: 'Curious', rhythm: 'drift',
      instrument: 'keys', layerInstrument: 'cello', harmony: 'third', layerLevel: 30, ambient: 'fire', tempo: 70,
      energy: 43, ambientLevel: 42, reverb: 47, echo: 10, space: 67, texture: 'warm', phrase: 'I can imagine more than one ending.'
    },
    rest: {
      title: 'Moon Rest', color: '#7756d9', colorName: 'Moon Violet', energyWord: 'Quiet', rhythm: 'flow',
      instrument: 'harp', layerInstrument: 'flute', harmony: 'floating', layerLevel: 22, ambient: 'night', tempo: 56,
      energy: 28, ambientLevel: 52, reverb: 62, echo: 14, space: 88, texture: 'airy', phrase: 'Rest is part of the adventure.'
    },
    create: {
      title: 'Maker Spark', color: '#ff8d3b', colorName: 'Maker Orange', energyWord: 'Playful', rhythm: 'spark',
      instrument: 'marimba', layerInstrument: 'guitar', harmony: 'fifth', layerLevel: 32, ambient: 'crystal', tempo: 108,
      energy: 75, ambientLevel: 28, reverb: 34, echo: 18, space: 70, texture: 'raw', phrase: 'I can experiment before I know the answer.'
    },
    proud: {
      title: 'Proud Stage', color: '#ff4fa3', colorName: 'Stage Pink', energyWord: 'Powerful', rhythm: 'spark',
      instrument: 'guitar', layerInstrument: 'drums', harmony: 'octave', layerLevel: 44, ambient: 'city', tempo: 124,
      energy: 90, ambientLevel: 24, reverb: 38, echo: 16, space: 92, texture: 'cinematic', phrase: 'My version is allowed to be mine.'
    }
  };

  const DEFAULT_STATE = {
    mode: 'easy', scene: 0, tool: 'color',
    title: 'My Sound + Color Moment', color: '#8a65ff', colorName: 'Violet', energyWord: 'Quiet',
    rhythm: 'heartbeat', instrument: 'keys', layerInstrument: 'none', harmony: 'fifth', layerLevel: 36,
    ambient: 'rain', tempo: 82, energy: 55, ambientLevel: 45, reverb: 28, echo: 12, space: 45,
    texture: 'warm', masterVolume: 86, phrase: '', voice: 'silent', includePhrase: false
  };

  const PATTERNS = {
    heartbeat: [1, .72, 0, 0, .88, .58, 0, 0],
    pulse: [.92, 0, .72, 0, .92, 0, .72, 0],
    bounce: [1, 0, .55, .72, 0, .8, .48, 0],
    flow: [.68, 0, 0, .45, .76, 0, 0, .5],
    spark: [.92, .35, 0, .72, .4, .8, 0, .62],
    drift: [.6, 0, .34, 0, .52, 0, .26, .42],
    orbit: [.78, .22, .46, 0, .72, .18, .56, .34]
  };

  class BacciSoundColorStudio {
    constructor(root) {
      this.root = root;
      this.root.dataset.version = VERSION;
      this.sectionId = root.dataset.sectionId || 'default';
      this.config = this.readConfig();
      this.scenes = (this.config.scenes || []).map((scene) => ({ ...scene, videos: this.parseLines(scene.videosText) }));
      this.realms = this.config.affirmationRealms || [];
      this.state = { ...DEFAULT_STATE };
      this.bound = [];
      this.currentScene = 0;
      this.currentClip = 0;
      this.sceneVideoTimer = null;
      this.sceneGapTimer = null;
      this.sceneVideoIntent = 'auto';
      this.videoSoundOn = false;
      this.isPlaying = false;
      this.soundEnabled = false;
      this.audioContext = null;
      this.mixBus = null;
      this.dryGain = null;
      this.reverbNode = null;
      this.reverbGain = null;
      this.delayNode = null;
      this.delayFeedback = null;
      this.delayGain = null;
      this.masterGain = null;
      this.compressor = null;
      this.analyser = null;
      this.patternPreviewTimer = null;
      this.heartbeatTimers = [];
      this.welcomeStage = 'heartbeat';
      this.themeAudio = null;
      this.themeTrack = 'full';
      this.sonicTimer = null;
      this.step = 0;
      this.nextNoteTime = 0;
      this.schedulerTimer = null;
      this.animationFrame = null;
      this.particleFrame = null;
      this.ambientNodes = [];
      this.noiseCache = new Map();
      this.recordedVoiceUrl = '';
      this.mediaRecorder = null;
      this.mediaStream = null;
      this.recordedChunks = [];
      this.recordingTimer = null;
      this.phrasePromptShown = false;
      this.toastTimer = null;
      this.realmFilter = 'all';
      this.activeRealm = null;
      this.breathingMode = 'quiet';
      this.breathTarget = 1;
      this.breathCompleted = 0;
      this.breathTimer = null;
      this.breathingActive = false;
      this.breathSoundOn = false;
      this.wasPlayingBeforeBreath = false;
      this.visibilityObserver = null;
      this.isVisible = true;
      this.pointerFrame = null;
      this.tiltFrame = null;
      this.swipeStart = null;
      this.saveData = Boolean(navigator.connection?.saveData);
      this.systemReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
      this.experienceStarted = !this.config.showWelcome;
      this.welcomeKey = `${this.config.storageKey || 'bk-sound-color-studio-v7'}:welcome:${VERSION}:${this.sectionId}`;

      this.refs = {
        theater: q(root, '[data-bk-scs4-theater]'),
        mediaShell: q(root, '[data-bk-scs4-media-shell]'),
        ambientArt: q(root, '[data-bk-scs4-ambient-art]'),
        poster: q(root, '[data-bk-scs4-poster]'),
        video: q(root, '[data-bk-scs4-video]'),
        bringAlive: q(root, '[data-bk-scs4-bring-alive]'),
        particles: q(root, '[data-bk-scs4-particles]'),
        sequencer: q(root, '[data-bk-scs4-sequencer]'),
        mixCanvas: q(root, '[data-bk-scs4-mix-canvas]'),
        visualizer: q(root, '[data-bk-scs4-visualizer]'),
        cardWave: q(root, '[data-bk-scs4-card-wave]'),
        cardCanvas: q(root, '[data-bk-scs4-card-canvas]'),
        status: q(root, '[data-bk-scs4-status]'),
        breathDialog: q(root, '[data-bk-scs4-breathing-dialog]'),
        breathVideo: q(root, '[data-bk-scs4-breathing-video]'),
        breathPoster: q(root, '[data-bk-scs4-breathing-poster]'),
        breathMedia: q(root, '.bk-scs4__breathing-media'),
        breathCue: q(root, '[data-bk-scs4-breathing-cue]'),
        phoenixVideo: q(root, '[data-bk-scs4-phoenix-video]'),
        welcome: q(root, '[data-bk-scs4-welcome]'),
        songDialog: q(root, '[data-bk-scs7-song-dialog]')
      };

      this.init();
    }

    readConfig() {
      const node = q(this.root, '[data-bk-scs4-config]');
      try { return JSON.parse(node?.textContent || '{}'); }
      catch (error) {
        console.error('Bacci Kid Sound + Color Studio configuration error', error);
        return { scenes: [], affirmationRealms: [], breathing: {} };
      }
    }

    parseLines(value) {
      return String(value || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
    }

    bind(node, event, handler, options) {
      if (!node) return;
      node.addEventListener(event, handler, options);
      this.bound.push([node, event, handler, options]);
    }

    init() {
      if (this.saveData) this.root.classList.add('is-save-data');
      if (this.systemReducedMotion) this.root.classList.add('is-reduced-motion');
      try {
        if (!this.systemReducedMotion && localStorage.getItem(`${this.config.storageKey}:motion`) === 'reduced') this.root.classList.add('is-reduced-motion');
      } catch (_) {}
      this.updateMotionButton();
      this.applyPageChrome();
      this.restoreState();
      this.bindEvents();
      this.setupWelcome();
      this.renderRealms();
      this.applyState(this.state, { persist: false, restorePhrase: true });
      this.setMode(this.state.mode || 'easy', { announce: false, persist: false });
      this.setTool(this.state.tool || 'color', { announce: false, persist: false });
      this.goToScene(Number(this.state.scene || 0), { announce: false, focus: false, immediate: true });
      this.drawSequencer();
      this.drawMixCanvas();
      this.drawIdleVisualizer();
      this.drawCardWave();
      this.updateCardPreview();
      this.setupVisibilityObserver();
      this.setupStageTilt();
      this.setupSwipe();
      this.startParticles();
      this.prefetchScenes(0);
      this.updateMediaControls();
      requestAnimationFrame(() => this.root.classList.add('is-ready'));
    }

    applyPageChrome() {
      if (this.config.hideStoreChrome) document.body.classList.add('bk-scs4-page');
    }

    setupWelcome() {
      const welcome = this.refs.welcome;
      if (!welcome || !this.config.showWelcome) {
        this.experienceStarted = true;
        this.root.classList.add('is-started');
        if (welcome) welcome.hidden = true;
        return;
      }
      let seen = false;
      try { seen = sessionStorage.getItem(this.welcomeKey) === '1'; } catch (_) {}
      this.experienceStarted = seen;
      this.root.classList.toggle('is-started', seen);
      document.body.classList.toggle('bk-scs4-welcome-open', !seen);
      welcome.hidden = seen;
      if (!seen) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        this.pauseSceneVideo({ preservePoster: true });
        const firstStage = this.config.enableHeartbeatIntro === false ? (this.config.showAccessibilityEntrance === false ? 'route' : 'access') : 'heartbeat';
        this.showWelcomeStage(firstStage);
        if (firstStage === 'heartbeat') this.startHeartbeatSequence();
        requestAnimationFrame(() => {
          const focusTarget = q(welcome, firstStage === 'heartbeat' ? '[data-bk-scs7-add-beat]' : firstStage === 'access' ? '[data-bk-scs7-entrance-mode]' : '[data-bk-scs4-start-easy]');
          focusTarget?.focus({ preventScroll: true });
        });
      }
    }

    clearHeartbeatTimers() {
      this.heartbeatTimers.forEach((timer) => window.clearTimeout(timer));
      this.heartbeatTimers = [];
    }

    showWelcomeStage(stage) {
      this.welcomeStage = stage;
      qa(this.root, '[data-bk-scs7-welcome-stage]').forEach((panel) => {
        const active = panel.dataset.bkScs7WelcomeStage === stage;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      this.refs.welcome?.setAttribute('data-stage', stage);
    }

    startHeartbeatSequence() {
      this.clearHeartbeatTimers();
      const messages = qa(this.root, '[data-bk-scs7-heartbeat-message]');
      const activate = (index) => messages.forEach((message, messageIndex) => message.classList.toggle('is-active', messageIndex === index));
      if (this.reduced()) {
        activate(2);
        this.refs.welcome?.classList.add('is-heartbeat-final');
        return;
      }
      activate(0);
      this.heartbeatTimers.push(window.setTimeout(() => activate(1), 1800));
      this.heartbeatTimers.push(window.setTimeout(() => {
        activate(2);
        this.refs.welcome?.classList.add('is-heartbeat-final');
      }, 5100));
    }

    addMyBeat() {
      this.clearHeartbeatTimers();
      qa(this.root, '[data-bk-scs7-heartbeat-message]').forEach((message, index) => message.classList.toggle('is-active', index === 2));
      this.refs.welcome?.classList.add('is-heartbeat-final', 'is-beat-added');
      this.announce('Your beat joined the Bacci Kid heartbeat.');
      window.setTimeout(() => {
        this.showWelcomeStage(this.config.showAccessibilityEntrance === false ? 'route' : 'access');
      }, this.reduced() ? 120 : 850);
    }

    chooseEntranceMode(mode) {
      this.root.classList.remove('is-calm-mode', 'is-reduced-motion');
      if (mode === 'calm') {
        this.root.classList.add('is-calm-mode');
        qa(this.root, '[data-bk-scs4-master-volume]').forEach((input) => { input.value = 55; });
        this.syncMasterVolumeInputs(q(this.root, '[data-bk-scs4-master-volume]'));
      }
      if (mode === 'reduced') {
        this.root.classList.add('is-reduced-motion');
        this.pauseSceneVideo({ preservePoster: true });
      }
      this.updateCalmButtons();
      this.updateMotionButton();
      this.showWelcomeStage('route');
      this.announce(mode === 'calm' ? 'Calm entrance selected.' : mode === 'reduced' ? 'Reduced Motion entrance selected.' : 'Standard entrance selected.');
      requestAnimationFrame(() => q(this.root, '[data-bk-scs4-start-easy]')?.focus({ preventScroll: true }));
    }

    skipHeartbeat() {
      this.clearHeartbeatTimers();
      this.showWelcomeStage(this.config.showAccessibilityEntrance === false ? 'route' : 'access');
    }

    startExperience(mode = 'easy', options = {}) {
      this.experienceStarted = true;
      try { sessionStorage.setItem(this.welcomeKey, '1'); } catch (_) {}
      document.body.classList.remove('bk-scs4-welcome-open');
      this.root.classList.add('is-started');
      if (this.refs.welcome) this.refs.welcome.hidden = true;
      this.setMode(mode, { announce: false });
      if (options.intro) {
        this.goToScene(0, { focus: false, announce: false });
        this.scrollToTheater();
        window.setTimeout(() => this.startSceneVideo({ manual: true, restart: true }), 260);
        this.announce('Cinematic introduction started. Video begins muted; use Video Sound when you choose.');
        return;
      }
      if (options.explore) {
        this.scrollToTheater();
        if (this.sceneVideos().length && this.config.autoPlaySceneVideos && !this.reduced() && !this.saveData) this.scheduleSceneVideo();
        this.announce('Explore at your own pace. Easy Start and Studio Pro are both available.');
        return;
      }
      this.scrollToConsole();
      if (this.sceneVideos().length && this.config.autoPlaySceneVideos && !this.reduced() && !this.saveData) this.scheduleSceneVideo();
      this.announce(mode === 'pro' ? 'Studio Pro is open. Start with Color or choose any tool.' : 'Easy Start is open. Choose a color, a vibe, and a sound world.');
    }

    bindEvents() {
      qa(this.root, '[data-bk-scs4-scene]').forEach((button) => this.bind(button, 'click', () => this.goToScene(Number(button.dataset.bkScs4Scene))));
      this.bind(q(this.root, '[data-bk-scs4-prev]'), 'click', () => this.goToScene(this.currentScene - 1));
      this.bind(q(this.root, '[data-bk-scs4-next]'), 'click', () => {
        if (this.currentScene >= this.scenes.length - 1) this.openCompletion();
        else this.goToScene(this.currentScene + 1);
      });

      qa(this.root, '[data-bk-scs4-mode]').forEach((button) => this.bind(button, 'click', () => this.setMode(button.dataset.bkScs4Mode)));
      this.bind(q(this.root, '[data-bk-scs4-mode-toggle]'), 'click', () => this.setMode(this.state.mode === 'easy' ? 'pro' : 'easy'));
      this.bind(q(this.root, '[data-bk-scs4-start-easy]'), 'click', () => this.startExperience('easy'));
      this.bind(q(this.root, '[data-bk-scs4-start-pro]'), 'click', () => this.startExperience('pro'));
      this.bind(q(this.root, '[data-bk-scs4-watch-intro]'), 'click', () => this.startExperience('easy', { intro: true }));
      this.bind(q(this.root, '[data-bk-scs4-dismiss-welcome]'), 'click', () => this.startExperience(this.state.mode || 'easy', { explore: true }));
      this.bind(q(this.root, '[data-bk-scs7-add-beat]'), 'click', () => this.addMyBeat());
      this.bind(q(this.root, '[data-bk-scs7-skip-heartbeat]'), 'click', () => this.skipHeartbeat());
      qa(this.root, '[data-bk-scs7-entrance-mode]').forEach((button) => this.bind(button, 'click', () => this.chooseEntranceMode(button.dataset.bkScs7EntranceMode)));
      qa(this.root, '[data-bk-scs7-open-song]').forEach((button) => this.bind(button, 'click', () => this.openSongPlayer()));
      this.bind(q(this.root, '[data-bk-scs7-play-sonic-logo]'), 'click', () => this.playSonicLogo());
      qa(this.root, '[data-bk-scs7-song-track]').forEach((button) => this.bind(button, 'click', () => this.selectThemeTrack(button.dataset.bkScs7SongTrack)));
      this.bind(q(this.root, '[data-bk-scs7-song-play]'), 'click', () => this.playThemeSong());
      this.bind(q(this.root, '[data-bk-scs7-song-pause]'), 'click', () => this.pauseThemeSong());
      this.bind(q(this.root, '[data-bk-scs7-song-stop]'), 'click', () => this.stopThemeSong());
      this.bind(q(this.root, '[data-bk-scs7-song-volume]'), 'input', (event) => this.setThemeSongVolume(event.target.value));
      const toolTabs = qa(this.root, '[data-bk-scs4-tool]');
      toolTabs.forEach((button) => this.bind(button, 'click', () => this.setTool(button.dataset.bkScs4Tool)));
      this.bind(q(this.root, '.bk-scs4__tool-tabs'), 'keydown', (event) => {
        if (!['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const current = Math.max(0, toolTabs.indexOf(document.activeElement));
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? toolTabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + toolTabs.length) % toolTabs.length;
        toolTabs[next]?.focus();
        toolTabs[next]?.click();
      });

      qa(this.root, '[data-bk-scs4-sound]').forEach((button) => this.bind(button, 'click', () => this.toggleSound()));
      qa(this.root, '[data-bk-scs4-open-guide]').forEach((button) => this.bind(button, 'click', () => this.openDialog('guide')));
      this.bind(q(this.root, '[data-bk-scs4-history-back]'), 'click', () => this.goBack());
      qa(this.root, '[data-bk-scs4-calm]').forEach((button) => this.bind(button, 'click', () => this.toggleCalm()));
      this.bind(q(this.root, '[data-bk-scs4-motion]'), 'click', () => this.toggleMotion());
      this.bind(q(this.root, '[data-bk-scs4-reveal-art]'), 'click', () => this.toggleRevealArt());
      this.bind(q(this.root, '[data-bk-scs4-fullscreen]'), 'click', () => this.toggleFullscreen(this.refs.theater));
      this.bind(q(this.root, '[data-bk-scs4-fullscreen-performance]'), 'click', () => this.toggleFullscreen(q(this.root, '[data-bk-scs4-performance-stage]')));

      this.bind(q(this.root, '[data-bk-scs4-video-play]'), 'click', () => this.toggleSceneVideo());
      this.bind(q(this.root, '[data-bk-scs4-video-replay]'), 'click', () => this.replaySceneVideo());
      this.bind(q(this.root, '[data-bk-scs4-video-next]'), 'click', () => this.nextSceneClip(true));
      this.bind(q(this.root, '[data-bk-scs4-video-sound]'), 'click', () => this.toggleVideoSound());
      this.bind(this.refs.bringAlive, 'click', () => this.startSceneVideo({ manual: true, restart: true }));
      this.bind(this.refs.video, 'ended', () => this.handleSceneVideoEnded());
      this.bind(this.refs.video, 'play', () => { this.root.classList.add('is-video-playing'); this.updateMediaControls(); });
      this.bind(this.refs.video, 'pause', () => { this.root.classList.remove('is-video-playing'); this.updateMediaControls(); });
      this.bind(this.refs.video, 'error', () => this.showPoster('Video unavailable. The complete poster remains visible.'));

      qa(this.root, '[data-bk-scs4-open-checkin]').forEach((button) => this.bind(button, 'click', () => this.openDialog('checkin')));
      this.bind(q(this.root, '[data-bk-scs4-open-trust]'), 'click', () => this.openDialog('trust'));
      this.bind(q(this.root, '[data-bk-scs4-open-library]'), 'click', () => this.openLibrary());
      this.bind(q(this.root, '[data-bk-scs4-open-realms]'), 'click', () => this.scrollToRealms());
      qa(this.root, '[data-bk-scs4-checkin-action]').forEach((button) => this.bind(button, 'click', () => this.handleCheckIn(button.dataset.bkScs4CheckinAction)));

      this.bind(q(this.root, '[data-bk-scs4-easy-launch]'), 'click', () => this.launchEasyStart());
      qa(this.root, '[data-bk-scs4-surprise]').forEach((button) => this.bind(button, 'click', () => this.randomize()));

      const customColor = q(this.root, '[data-bk-scs4-custom-color]');
      this.bind(customColor, 'input', (event) => this.activateCustomColor(event.target.value));
      qa(this.root, 'input[name^="bk-scs4-color-"]').forEach((input) => this.bind(input, 'change', () => {
        if (customColor) customColor.dataset.active = 'false';
        this.syncEasyFromPro(); this.updateFromControls();
      }));
      qa(this.root, 'input[name^="bk-scs4-energy-word-"],input[name^="bk-scs4-rhythm-"],input[name^="bk-scs4-instrument-"],input[name^="bk-scs4-ambient-"],input[name^="bk-scs4-texture-"]').forEach((input) => this.bind(input, 'change', () => {
        this.updateFromControls();
        if (input.name.includes('ambient') && this.isPlaying) this.restartAmbient();
      }));
      qa(this.root, 'input[name^="bk-scs4-easy-color-"],input[name^="bk-scs4-easy-vibe-"],input[name^="bk-scs4-easy-world-"]').forEach((input) => this.bind(input, 'change', () => this.syncProFromEasy(false)));

      ['tempo','energy','ambient-level','layer-level','reverb','echo','space','master-volume'].forEach((key) => {
        qa(this.root, `[data-bk-scs4-${key}]`).forEach((input) => this.bind(input, 'input', () => {
          if (key === 'master-volume') this.syncMasterVolumeInputs(input);
          this.updateOutputs(); this.updateFromControls();
          if (this.isPlaying && key === 'ambient-level') this.restartAmbient();
          if (this.isPlaying && ['reverb','echo','space'].includes(key)) this.updateEffects(this.state);
          if (key === 'master-volume') this.applyMasterVolume();
        }));
      });
      this.bind(q(this.root, '[data-bk-scs4-layer-instrument]'), 'change', () => this.updateFromControls());
      this.bind(q(this.root, '[data-bk-scs4-harmony]'), 'change', () => this.updateFromControls());
      this.bind(q(this.root, '[data-bk-scs4-title-input]'), 'input', () => this.updateFromControls());

      const phrase = q(this.root, '[data-bk-scs4-phrase]');
      this.bind(phrase, 'input', () => {
        this.checkDirectPhrase(phrase.value);
        this.updateFromControls();
      });
      this.bind(q(this.root, '[data-bk-scs4-include-phrase]'), 'change', () => this.updateFromControls());
      this.bind(q(this.root, '[data-bk-scs4-voice]'), 'change', () => this.updateFromControls());
      qa(this.root, '[data-bk-scs4-affirmation]').forEach((button) => this.bind(button, 'click', () => {
        if (phrase) phrase.value = button.dataset.bkScs4Affirmation || button.textContent.trim();
        this.updateFromControls();
        this.announce('Words added. Edit them, keep them, or leave the field blank.');
      }));
      this.bind(q(this.root, '[data-bk-scs4-record]'), 'click', () => this.toggleRecording());

      qa(this.root, '[data-bk-scs4-instrument-filter]').forEach((button) => this.bind(button, 'click', () => this.filterInstruments(button.dataset.bkScs4InstrumentFilter)));
      qa(this.root, '[data-bk-scs4-pattern-preview]').forEach((button) => this.bind(button, 'click', () => this.previewPattern(button.dataset.bkScs4PatternPreview, button)));
      qa(this.root, '[data-bk-scs4-beat-pad]').forEach((button) => this.bind(button, 'click', () => this.triggerBeatPad(Number(button.dataset.bkScs4BeatPad), button)));

      qa(this.root, '[data-bk-scs4-play]').forEach((button) => this.bind(button, 'click', () => this.togglePlay()));
      qa(this.root, '[data-bk-scs4-stop]').forEach((button) => this.bind(button, 'click', () => this.stopMix()));
      qa(this.root, '[data-bk-scs4-save]').forEach((button) => this.bind(button, 'click', () => this.downloadSoundCard()));
      this.bind(q(this.root, '[data-bk-scs4-save-library]'), 'click', () => this.saveToLibrary());
      this.bind(q(this.root, '[data-bk-scs4-clear-library]'), 'click', () => this.clearLibrary());
      this.bind(q(this.root, '[data-bk-scs4-reset]'), 'click', () => this.resetExperience());

      qa(this.root, '[data-bk-scs4-realm-filter]').forEach((button) => this.bind(button, 'click', () => this.filterRealms(button.dataset.bkScs4RealmFilter)));
      this.bind(q(this.root, '[data-bk-scs4-load-realm-sound]'), 'click', () => this.loadActiveRealm());

      qa(this.root, '[data-bk-scs4-breathing-mode]').forEach((button) => this.bind(button, 'click', () => this.setBreathingMode(button.dataset.bkScs4BreathingMode)));
      qa(this.root, '[data-bk-scs4-breath-count]').forEach((button) => this.bind(button, 'click', () => this.setBreathCount(Number(button.dataset.bkScs4BreathCount))));
      this.bind(q(this.root, '[data-bk-scs4-breath-play]'), 'click', () => this.startBreathing());
      this.bind(q(this.root, '[data-bk-scs4-breath-pause]'), 'click', () => this.pauseBreathing());
      this.bind(q(this.root, '[data-bk-scs4-breath-sound]'), 'click', () => this.toggleBreathingSound());
      this.bind(q(this.root, '[data-bk-scs4-breath-end]'), 'click', () => this.endBreathing());
      this.bind(this.refs.breathVideo, 'ended', () => { if (this.breathingActive) this.refs.breathVideo.play().catch(() => {}); });

      this.bind(q(this.root, '[data-bk-scs4-completion-replay]'), 'click', () => this.playPhoenix());
      this.bind(this.refs.phoenixVideo, 'click', () => this.playPhoenix());

      this.bind(q(this.root, '[data-bk-scs4-mobile-scenes]'), 'click', () => this.scrollToTheater());
      this.bind(q(this.root, '[data-bk-scs4-mobile-mix]'), 'click', () => { this.setMode('pro'); this.setTool('mix'); this.scrollToConsole(); });

      this.bind(document, 'keydown', (event) => this.handleKeydown(event));
      this.bind(document, 'visibilitychange', () => this.handleVisibilityChange());
      this.bind(window, 'resize', () => this.resizeCanvases(), { passive: true });
      this.bind(document, 'fullscreenchange', () => this.root.classList.toggle('is-fullscreen', Boolean(document.fullscreenElement)));

      qa(this.root, 'dialog').forEach((dialog) => this.bind(dialog, 'close', () => {
        if (dialog === this.refs.breathDialog) this.cleanupBreathing();
        if (dialog === q(this.root, '[data-bk-scs4-completion-dialog]')) this.refs.phoenixVideo?.pause();
      }));
    }

    inputName(prefix) { return `${prefix}-${this.sectionId}`; }
    selected(prefix, fallback) { return q(this.root, `input[name="${this.inputName(prefix)}"]:checked`)?.value || fallback; }

    getStateFromControls() {
      const colorRadio = q(this.root, `input[name="${this.inputName('bk-scs4-color')}"]:checked`);
      const custom = q(this.root, '[data-bk-scs4-custom-color]');
      const customActive = custom?.dataset.active === 'true';
      const phrase = q(this.root, '[data-bk-scs4-phrase]')?.value.trim() || '';
      return {
        ...this.state,
        title: q(this.root, '[data-bk-scs4-title-input]')?.value.trim() || 'My Sound + Color Moment',
        color: customActive ? custom.value : (colorRadio?.value || '#8a65ff'),
        colorName: customActive ? 'Custom' : (colorRadio?.dataset.name || 'Violet'),
        energyWord: this.selected('bk-scs4-energy-word', 'Quiet'),
        rhythm: this.selected('bk-scs4-rhythm', 'heartbeat'),
        instrument: this.selected('bk-scs4-instrument', 'keys'),
        layerInstrument: q(this.root, '[data-bk-scs4-layer-instrument]')?.value || 'none',
        harmony: q(this.root, '[data-bk-scs4-harmony]')?.value || 'fifth',
        layerLevel: Number(q(this.root, '[data-bk-scs4-layer-level]')?.value || 36),
        ambient: this.selected('bk-scs4-ambient', 'rain'),
        tempo: Number(q(this.root, '[data-bk-scs4-tempo]')?.value || 82),
        energy: Number(q(this.root, '[data-bk-scs4-energy]')?.value || 55),
        ambientLevel: Number(q(this.root, '[data-bk-scs4-ambient-level]')?.value || 45),
        reverb: Number(q(this.root, '[data-bk-scs4-reverb]')?.value || 28),
        echo: Number(q(this.root, '[data-bk-scs4-echo]')?.value || 12),
        space: Number(q(this.root, '[data-bk-scs4-space]')?.value || 45),
        texture: this.selected('bk-scs4-texture', 'warm'),
        masterVolume: Number(q(this.root, '[data-bk-scs4-master-volume]')?.value || this.config.defaultMasterVolume || 86),
        phrase,
        voice: q(this.root, '[data-bk-scs4-voice]')?.value || 'silent',
        includePhrase: Boolean(q(this.root, '[data-bk-scs4-include-phrase]')?.checked)
      };
    }

    updateFromControls() {
      this.state = this.getStateFromControls();
      this.updateOutputs();
      this.updateLiveState();
      this.drawSequencer();
      this.drawMixCanvas();
      this.drawCardWave();
      this.updateCardPreview();
      this.persistState();
    }

    setRadio(prefix, value) {
      const expected = String(value);
      const target = qa(this.root, `input[name="${this.inputName(prefix)}"]`).find((input) => input.value === expected);
      if (target) target.checked = true;
    }

    applyState(state, options = {}) {
      if (!state) return;
      const merged = { ...DEFAULT_STATE, ...state };
      this.state = merged;
      const custom = q(this.root, '[data-bk-scs4-custom-color]');
      const colorInputs = qa(this.root, `input[name="${this.inputName('bk-scs4-color')}"]`);
      const exact = colorInputs.find((input) => input.value.toLowerCase() === String(merged.color).toLowerCase());
      if (exact) {
        exact.checked = true;
        if (custom) custom.dataset.active = 'false';
      } else if (custom) {
        custom.value = merged.color;
        custom.dataset.active = 'true';
        colorInputs.forEach((input) => { input.checked = false; });
      }
      this.setRadio('bk-scs4-energy-word', merged.energyWord);
      this.setRadio('bk-scs4-rhythm', merged.rhythm);
      this.setRadio('bk-scs4-instrument', merged.instrument);
      this.setRadio('bk-scs4-ambient', merged.ambient);
      this.setRadio('bk-scs4-texture', merged.texture);

      const assign = (selector, value) => { const node = q(this.root, selector); if (node && value !== undefined && value !== null) node.value = value; };
      assign('[data-bk-scs4-title-input]', merged.title);
      assign('[data-bk-scs4-layer-instrument]', merged.layerInstrument);
      assign('[data-bk-scs4-harmony]', merged.harmony);
      assign('[data-bk-scs4-layer-level]', merged.layerLevel);
      assign('[data-bk-scs4-tempo]', merged.tempo);
      assign('[data-bk-scs4-energy]', merged.energy);
      assign('[data-bk-scs4-ambient-level]', merged.ambientLevel);
      assign('[data-bk-scs4-reverb]', merged.reverb);
      assign('[data-bk-scs4-echo]', merged.echo);
      assign('[data-bk-scs4-space]', merged.space);
      qa(this.root, '[data-bk-scs4-master-volume]').forEach((node) => { node.value = merged.masterVolume ?? this.config.defaultMasterVolume ?? 86; });
      assign('[data-bk-scs4-voice]', merged.voice);
      if (options.restorePhrase) assign('[data-bk-scs4-phrase]', merged.phrase || '');
      const include = q(this.root, '[data-bk-scs4-include-phrase]');
      if (include) include.checked = Boolean(options.restorePhrase && merged.includePhrase);
      this.syncEasyFromPro();
      this.updateOutputs();
      this.updateLiveState();
      this.drawSequencer();
      this.drawMixCanvas();
      this.drawCardWave();
      this.updateCardPreview();
      if (this.isPlaying) { this.updateEffects(merged); this.restartAmbient(); }
      if (options.persist !== false) this.persistState();
    }

    activateCustomColor(value) {
      const custom = q(this.root, '[data-bk-scs4-custom-color]');
      if (custom) custom.dataset.active = 'true';
      qa(this.root, `input[name="${this.inputName('bk-scs4-color')}"]`).forEach((input) => { input.checked = false; });
      this.updateFromControls();
    }

    syncEasyFromPro() {
      const state = this.getStateFromControls();
      const easyColor = qa(this.root, `input[name="${this.inputName('bk-scs4-easy-color')}"]`).find((input) => input.value.toLowerCase() === state.color.toLowerCase());
      if (easyColor) easyColor.checked = true;
      const easyVibe = qa(this.root, `input[name="${this.inputName('bk-scs4-easy-vibe')}"]`).find((input) => input.value === state.energyWord);
      if (easyVibe) easyVibe.checked = true;
    }

    syncProFromEasy(persist = true) {
      const easyColor = q(this.root, `input[name="${this.inputName('bk-scs4-easy-color')}"]:checked`);
      const easyVibe = q(this.root, `input[name="${this.inputName('bk-scs4-easy-vibe')}"]:checked`);
      if (easyColor) {
        const proColor = qa(this.root, `input[name="${this.inputName('bk-scs4-color')}"]`).find((input) => input.value === easyColor.value);
        if (proColor) proColor.checked = true;
        const custom = q(this.root, '[data-bk-scs4-custom-color]');
        if (custom) custom.dataset.active = 'false';
      }
      if (easyVibe) this.setRadio('bk-scs4-energy-word', easyVibe.value);
      if (persist) this.updateFromControls();
    }

    launchEasyStart() {
      const world = q(this.root, `input[name="${this.inputName('bk-scs4-easy-world')}"]:checked`)?.value || 'midnight';
      const easyColor = q(this.root, `input[name="${this.inputName('bk-scs4-easy-color')}"]:checked`);
      const easyVibe = q(this.root, `input[name="${this.inputName('bk-scs4-easy-vibe')}"]:checked`);
      if (world === 'surprise') this.randomize({ announce: false });
      else this.loadPreset(world, { announce: false, navigate: false });
      if (easyColor) {
        const proColor = qa(this.root, `input[name="${this.inputName('bk-scs4-color')}"]`).find((input) => input.value === easyColor.value);
        if (proColor) proColor.checked = true;
      }
      if (easyVibe) this.setRadio('bk-scs4-energy-word', easyVibe.value);
      this.updateFromControls();
      this.setMode('pro', { announce: false });
      this.setTool('perform', { announce: false });
      this.goToScene(8, { focus: false });
      this.scrollToConsole();
      this.togglePlay(true);
      this.announce('Your Easy Start mix is ready. Bacci is performing it now.');
    }

    setMode(mode, options = {}) {
      const next = mode === 'pro' ? 'pro' : 'easy';
      this.state.mode = next;
      this.root.dataset.mode = next;
      q(this.root, '[data-bk-scs4-easy]')?.toggleAttribute('hidden', next !== 'easy');
      q(this.root, '[data-bk-scs4-pro]')?.toggleAttribute('hidden', next !== 'pro');
      qa(this.root, '[data-bk-scs4-mode]').forEach((button) => {
        const active = button.dataset.bkScs4Mode === next;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const top = q(this.root, '[data-bk-scs4-mode-toggle]');
      const label = q(this.root, '[data-bk-scs4-mode-label]');
      if (top) top.setAttribute('aria-pressed', String(next === 'pro'));
      if (label) label.textContent = next === 'pro' ? 'Easy Start' : 'Studio Pro';
      if (options.persist !== false) this.persistState();
      if (options.announce !== false) this.announce(next === 'pro' ? 'Studio Pro is open.' : 'Easy Start is open.');
    }

    setTool(tool, options = {}) {
      const valid = ['color','rhythm','instrument','ambient','energy','words','mix','perform','vault'];
      const next = valid.includes(tool) ? tool : 'color';
      this.state.tool = next;
      qa(this.root, '[data-bk-scs4-tool]').forEach((button) => {
        const active = button.dataset.bkScs4Tool === next;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      qa(this.root, '[data-bk-scs4-tool-panel]').forEach((panel) => {
        const active = panel.dataset.bkScs4ToolPanel === next;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (next === 'perform') this.drawIdleVisualizer();
      if (next === 'vault') this.updateCardPreview();
      if (options.persist !== false) this.persistState();
      if (options.announce !== false) this.announce(`${this.titleCase(next)} tools are open.`);
    }

    updateOutputs() {
      const pairs = [
        ['tempo', ' BPM'], ['energy', '%'], ['ambient-level', '%'], ['layer', '%'], ['reverb', '%'], ['echo', '%'], ['space', '%'], ['master-volume', '%']
      ];
      pairs.forEach(([key, suffix]) => {
        const inputKey = key === 'layer' ? 'layer-level' : key;
        const value = Number(q(this.root, `[data-bk-scs4-${inputKey}]`)?.value || 0);
        const outputs = qa(this.root, `[data-bk-scs4-${key}-output]`);
        outputs.forEach((output) => { output.textContent = `${value}${suffix}`; });
      });
    }

    updateLiveState() {
      const state = this.getStateFromControls();
      this.state = state;
      this.root.style.setProperty('--bk-scs4-live-color', state.color);
      this.root.style.setProperty('--bk-scs4-live-energy', String(state.energy / 100));
      const performanceTitle = q(this.root, '[data-bk-scs4-performance-title]');
      if (performanceTitle) performanceTitle.textContent = state.title;
      this.renderMixSummary(state);
      this.updateCardPreview(state);
    }

    renderMixSummary(state = this.state) {
      const node = q(this.root, '[data-bk-scs4-mix-summary]');
      if (!node) return;
      const values = [
        state.colorName, this.titleCase(state.rhythm), this.titleCase(state.instrument),
        state.layerInstrument !== 'none' ? `+ ${this.titleCase(state.layerInstrument)}` : null,
        this.titleCase(state.ambient), `${state.tempo} BPM`, `${state.energy}% energy`, this.titleCase(state.texture)
      ].filter(Boolean);
      node.innerHTML = values.map((value) => `<span>${escapeHtml(value)}</span>`).join('');
    }

    loadPreset(name, options = {}) {
      const preset = PRESETS[name];
      if (!preset) return;
      this.applyState({ ...this.state, ...preset }, { restorePhrase: true });
      if (options.navigate !== false) { this.setMode('pro', { announce: false }); this.setTool('mix', { announce: false }); this.goToScene(5, { focus: false }); this.scrollToConsole(); }
      if (options.announce !== false) this.announce(`${preset.title} loaded. Change anything to make it yours.`);
    }

    randomize(options = {}) {
      const colorInputs = qa(this.root, `input[name="${this.inputName('bk-scs4-color')}"]`);
      const chooseRadio = (prefix) => {
        const inputs = qa(this.root, `input[name="${this.inputName(prefix)}"]`);
        if (inputs.length) randomItem(inputs).checked = true;
      };
      if (colorInputs.length) randomItem(colorInputs).checked = true;
      const custom = q(this.root, '[data-bk-scs4-custom-color]');
      if (custom) custom.dataset.active = 'false';
      ['bk-scs4-energy-word','bk-scs4-rhythm','bk-scs4-instrument','bk-scs4-ambient','bk-scs4-texture'].forEach(chooseRadio);
      const layers = ['none','harp','flute','cello','chimes','glass-bells','kalimba','gayageum'];
      const layerSelect = q(this.root, '[data-bk-scs4-layer-instrument]');
      if (layerSelect) layerSelect.value = randomItem(layers);
      const harmony = q(this.root, '[data-bk-scs4-harmony]');
      if (harmony) harmony.value = randomItem(['same','octave','fifth','third','floating']);
      const ranges = {
        '[data-bk-scs4-tempo]': Math.floor(56 + Math.random() * 76),
        '[data-bk-scs4-energy]': Math.floor(28 + Math.random() * 70),
        '[data-bk-scs4-ambient-level]': Math.floor(15 + Math.random() * 67),
        '[data-bk-scs4-layer-level]': Math.floor(18 + Math.random() * 44),
        '[data-bk-scs4-reverb]': Math.floor(10 + Math.random() * 55),
        '[data-bk-scs4-echo]': Math.floor(0 + Math.random() * 38),
        '[data-bk-scs4-space]': Math.floor(20 + Math.random() * 75)
      };
      Object.entries(ranges).forEach(([selector, value]) => { const node = q(this.root, selector); if (node) node.value = String(value); });
      this.updateFromControls();
      if (this.isPlaying) { this.updateEffects(this.state); this.restartAmbient(); }
      if (options.announce !== false) this.announce('A new surprise mix is ready. Keep it, change it, or start again.');
    }

    /* ----------------------------------------------------------------------
       Scene and video engine
       ---------------------------------------------------------------------- */

    goToScene(index, options = {}) {
      const next = clamp(Number.isFinite(index) ? index : 0, 0, Math.max(0, this.scenes.length - 1));
      const scene = this.scenes[next];
      if (!scene) return;
      const update = () => this.applySceneDOM(next, scene, options);
      if (document.startViewTransition && !this.root.classList.contains('is-reduced-motion') && !options.immediate) {
        try { document.startViewTransition(update); } catch (_) { update(); }
      } else update();
      this.configureSceneMedia(scene, options);
      this.prefetchScenes(next);
    }

    applySceneDOM(next, scene, options) {
      this.currentScene = next;
      this.state.scene = next;
      this.root.dataset.currentScene = String(next);
      this.root.dataset.sceneSkin = scene.skin || 'studio';
      qa(this.root, '[data-bk-scs4-scene]').forEach((button, buttonIndex) => {
        const active = buttonIndex === next;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'step' : 'false');
        if (active) button.scrollIntoView({ behavior: this.reduced() ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      });
      const kicker = q(this.root, '[data-bk-scs4-scene-kicker]');
      const title = q(this.root, '[data-bk-scs4-scene-title]');
      const subtitle = q(this.root, '[data-bk-scs4-scene-subtitle]');
      if (kicker) kicker.textContent = `${String(scene.kicker || 'Create').toUpperCase()} · ${next + 1} OF ${this.scenes.length}`;
      if (title) title.textContent = scene.title || '';
      if (subtitle) subtitle.textContent = scene.subtitle || '';
      const bar = q(this.root, '[data-bk-scs4-progress-bar]');
      const copy = q(this.root, '[data-bk-scs4-progress-copy]');
      if (bar) bar.style.width = `${((next + 1) / Math.max(1, this.scenes.length)) * 100}%`;
      if (copy) copy.textContent = `${next + 1} of ${this.scenes.length}`;
      q(this.root, '[data-bk-scs4-prev]')?.toggleAttribute('disabled', next === 0);
      const nextButton = q(this.root, '[data-bk-scs4-next]');
      if (nextButton) nextButton.innerHTML = next === this.scenes.length - 1 ? '<span>Celebrate</span> ✦' : '<span>Next</span> →';
      if (options.focus !== false) title?.focus?.({ preventScroll: true });
      this.persistState();
      if (options.announce !== false) this.announce(`${scene.title}. Scene ${next + 1} of ${this.scenes.length}.`);
    }

    async configureSceneMedia(scene, options = {}) {
      window.clearTimeout(this.sceneVideoTimer);
      window.clearTimeout(this.sceneGapTimer);
      this.pauseSceneVideo({ preservePoster: true });
      this.currentClip = 0;
      this.sceneVideoIntent = 'auto';
      this.videoSoundOn = false;
      const posterUrl = scene.poster || '';
      this.root.classList.add('is-switching');
      const image = new Image();
      image.src = posterUrl;
      try { await image.decode(); } catch (_) {}
      if (this.refs.poster) {
        this.refs.poster.src = posterUrl;
        this.refs.poster.alt = `Bacci Kid ${scene.title}`;
      }
      if (this.refs.ambientArt) this.refs.ambientArt.src = posterUrl;
      this.root.classList.remove('is-switching');
      this.showPoster();
      this.updateMediaControls();

      const canAutoplay = this.experienceStarted && this.config.enableSceneVideos && scene.videos.length && this.config.autoPlaySceneVideos && !this.saveData && !this.reduced();
      if (canAutoplay) this.scheduleSceneVideo();
      else if (scene.videos.length) this.showBringAlive();
    }

    scheduleSceneVideo(delay = Number(this.config.posterHoldMs || 3500)) {
      window.clearTimeout(this.sceneVideoTimer);
      this.sceneVideoTimer = window.setTimeout(() => {
        if (!this.isVisible || document.hidden || this.root.classList.contains('is-calm-mode')) { this.showBringAlive(); return; }
        this.startSceneVideo({ manual: false });
      }, delay);
    }

    sceneVideos() { return this.scenes[this.currentScene]?.videos || []; }

    async startSceneVideo({ manual = false, restart = false } = {}) {
      const videos = this.sceneVideos();
      if (!this.config.enableSceneVideos || !videos.length || !this.refs.video) return;
      if (manual) this.sceneVideoIntent = 'play';
      if (restart || !this.refs.video.src) {
        this.currentClip = clamp(this.currentClip, 0, videos.length - 1);
        this.refs.video.src = videos[this.currentClip];
        this.refs.video.load();
      }
      this.refs.video.muted = !this.videoSoundOn;
      this.refs.video.playsInline = true;
      try {
        await this.refs.video.play();
        this.root.classList.add('is-video-visible');
        if (this.refs.bringAlive) this.refs.bringAlive.hidden = true;
      } catch (_) {
        this.showBringAlive();
      }
      this.updateMediaControls();
    }

    toggleSceneVideo() {
      if (!this.sceneVideos().length) { this.announce('This scene is poster-led until its video is added.'); return; }
      if (this.refs.video?.paused || !this.root.classList.contains('is-video-visible')) this.startSceneVideo({ manual: true });
      else { this.sceneVideoIntent = 'pause'; this.refs.video.pause(); }
    }

    replaySceneVideo() {
      if (!this.sceneVideos().length || !this.refs.video) { this.announce('This scene is poster-led until its video is added.'); return; }
      this.refs.video.currentTime = 0;
      this.startSceneVideo({ manual: true });
    }

    nextSceneClip(manual = false) {
      const videos = this.sceneVideos();
      if (videos.length < 2) { this.announce(videos.length ? 'This scene currently has one video clip.' : 'This scene is poster-led until its video is added.'); return; }
      if (this.config.videoRotation === 'random') {
        let next = this.currentClip;
        while (next === this.currentClip && videos.length > 1) next = Math.floor(Math.random() * videos.length);
        this.currentClip = next;
      } else this.currentClip = (this.currentClip + 1) % videos.length;
      this.refs.video.src = videos[this.currentClip];
      this.refs.video.load();
      this.startSceneVideo({ manual, restart: false });
      this.announce(`Playing clip ${this.currentClip + 1} of ${videos.length}.`);
    }

    handleSceneVideoEnded() {
      if (this.config.returnToPoster) this.showPoster();
      const videos = this.sceneVideos();
      if (this.config.autoRotateSceneClips && videos.length > 1 && this.sceneVideoIntent !== 'pause' && !this.reduced() && !this.saveData) {
        window.clearTimeout(this.sceneGapTimer);
        this.sceneGapTimer = window.setTimeout(() => this.nextSceneClip(false), Number(this.config.clipGapMs || 1500));
      }
    }

    pauseSceneVideo({ preservePoster = false } = {}) {
      window.clearTimeout(this.sceneVideoTimer);
      this.refs.video?.pause();
      if (!preservePoster) this.showPoster();
    }

    showPoster(message = '') {
      this.root.classList.remove('is-video-visible','is-video-playing');
      if (this.refs.video) this.refs.video.pause();
      if (message) this.announce(message);
      this.updateMediaControls();
    }

    showBringAlive() {
      if (this.refs.bringAlive) this.refs.bringAlive.hidden = !this.sceneVideos().length;
    }

    toggleVideoSound() {
      if (!this.refs.video || !this.sceneVideos().length) return;
      this.videoSoundOn = !this.videoSoundOn;
      this.refs.video.muted = !this.videoSoundOn;
      if (this.videoSoundOn && this.isPlaying) this.stopMix();
      this.updateMediaControls();
      this.announce(this.videoSoundOn ? 'Scene video sound is on.' : 'Scene video sound is off.');
    }

    updateMediaControls() {
      const videos = this.sceneVideos();
      const hasVideo = videos.length > 0;
      const playing = hasVideo && this.refs.video && !this.refs.video.paused && this.root.classList.contains('is-video-visible');
      const play = q(this.root, '[data-bk-scs4-video-play]');
      const replay = q(this.root, '[data-bk-scs4-video-replay]');
      const next = q(this.root, '[data-bk-scs4-video-next]');
      const sound = q(this.root, '[data-bk-scs4-video-sound]');
      [play,replay,sound].forEach((button) => { if (button) button.disabled = !hasVideo; });
      if (next) next.disabled = videos.length < 2;
      if (play) play.innerHTML = playing ? '<span aria-hidden="true">⏸</span><em>Pause</em>' : '<span aria-hidden="true">▶</span><em>Play</em>';
      if (sound) {
        sound.setAttribute('aria-pressed', String(this.videoSoundOn));
        sound.setAttribute('aria-label', this.videoSoundOn ? 'Turn scene video sound off' : 'Turn scene video sound on');
        sound.innerHTML = this.videoSoundOn ? '<span aria-hidden="true">🔊</span><em>Video sound</em>' : '<span aria-hidden="true">🔇</span><em>Video sound</em>';
      }
      if (!hasVideo && this.refs.bringAlive) this.refs.bringAlive.hidden = true;
    }

    prefetchScenes(index) {
      if (this.saveData) return;
      [index - 1, index + 1].forEach((candidate) => {
        const url = this.scenes[candidate]?.poster;
        if (!url) return;
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
      });
    }

    toggleRevealArt() {
      const active = !this.root.classList.contains('is-reveal-art');
      this.root.classList.toggle('is-reveal-art', active);
      const button = q(this.root, '[data-bk-scs4-reveal-art]');
      button?.setAttribute('aria-pressed', String(active));
      this.announce(active ? 'Reveal Art mode is on.' : 'Studio controls restored.');
    }

    async toggleFullscreen(element) {
      const target = element || this.refs.theater;
      if (!target) return;
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else await target.requestFullscreen();
      } catch (_) { this.announce('Full screen is not available in this browser.'); }
    }

    /* ----------------------------------------------------------------------
       Web Audio studio
       ---------------------------------------------------------------------- */

    reduced() { return this.root.classList.contains('is-reduced-motion') || this.systemReducedMotion; }

    async ensureAudio() {
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) throw new Error('This browser does not support the Sound + Color Studio audio engine.');
        this.audioContext = new AudioContextClass();
        this.mixBus = this.audioContext.createGain();
        this.dryGain = this.audioContext.createGain();
        this.reverbNode = this.audioContext.createConvolver();
        this.reverbNode.buffer = this.makeImpulse(2.6, 2.2);
        this.reverbGain = this.audioContext.createGain();
        this.delayNode = this.audioContext.createDelay(1.4);
        this.delayFeedback = this.audioContext.createGain();
        this.delayGain = this.audioContext.createGain();
        this.masterGain = this.audioContext.createGain();
        this.compressor = this.audioContext.createDynamicsCompressor();
        this.compressor.threshold.value = -20;
        this.compressor.knee.value = 24;
        this.compressor.ratio.value = 5;
        this.compressor.attack.value = .004;
        this.compressor.release.value = .26;
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;
        this.analyser.smoothingTimeConstant = .84;

        this.mixBus.connect(this.dryGain).connect(this.masterGain);
        this.mixBus.connect(this.reverbNode).connect(this.reverbGain).connect(this.masterGain);
        this.mixBus.connect(this.delayNode).connect(this.delayGain).connect(this.masterGain);
        this.delayNode.connect(this.delayFeedback).connect(this.delayNode);
        this.masterGain.connect(this.compressor).connect(this.analyser).connect(this.audioContext.destination);
        this.masterGain.gain.value = .0001;
      }
      if (this.audioContext.state === 'suspended') await this.audioContext.resume();
      this.updateEffects(this.state);
    }

    makeImpulse(duration = 2.2, decay = 2) {
      const rate = this.audioContext.sampleRate;
      const length = Math.max(1, Math.floor(rate * duration));
      const impulse = this.audioContext.createBuffer(2, length, rate);
      for (let channel = 0; channel < 2; channel += 1) {
        const data = impulse.getChannelData(channel);
        for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
      return impulse;
    }

    updateEffects(state = this.state) {
      if (!this.audioContext) return;
      const now = this.audioContext.currentTime;
      this.dryGain.gain.setTargetAtTime(.98, now, .04);
      this.reverbGain.gain.setTargetAtTime((state.reverb / 100) * .68, now, .06);
      this.delayGain.gain.setTargetAtTime((state.echo / 100) * .42, now, .06);
      this.delayNode.delayTime.setTargetAtTime(.11 + (state.echo / 100) * .43, now, .06);
      this.delayFeedback.gain.setTargetAtTime(.12 + (state.echo / 100) * .36, now, .06);
    }

    outputLevel(state = this.state) {
      const volume = clamp(Number(state.masterVolume ?? this.config.defaultMasterVolume ?? 86), 0, 100) / 100;
      return (.18 + (state.energy / 100) * .34) * volume;
    }

    syncMasterVolumeInputs(source) {
      const value = clamp(Number(source?.value ?? this.state.masterVolume ?? this.config.defaultMasterVolume ?? 86), 0, 100);
      qa(this.root, '[data-bk-scs4-master-volume]').forEach((input) => { if (input !== source) input.value = value; });
      qa(this.root, '[data-bk-scs4-master-volume-output]').forEach((output) => { output.textContent = `${value}%`; });
      this.state.masterVolume = value;
    }
    applyMasterVolume() { if (this.masterGain && this.audioContext) this.masterGain.gain.setTargetAtTime(this.soundEnabled ? this.outputLevel(this.state) : .0001, this.audioContext.currentTime, .04); }
    goBack() { const fallback=this.config.previousFallbackUrl||this.config.braveHubUrl||this.config.homeUrl||'/'; if(window.history.length>1&&document.referrer)window.history.back();else window.location.assign(fallback); }
    async previewPattern(name, button) {
      const pattern=PATTERNS[name]||PATTERNS.heartbeat; if(this.patternPreviewTimer)clearTimeout(this.patternPreviewTimer);
      try { await this.ensureAudio(); this.soundEnabled=true; this.state.masterVolume=Number(q(this.root,'[data-bk-scs4-master-volume]')?.value||this.state.masterVolume||86); this.masterGain.gain.setTargetAtTime(this.outputLevel(this.state),this.audioContext.currentTime,.035);
        qa(this.root,'[data-bk-scs4-sound]').forEach((node)=>{node.setAttribute('aria-pressed','true');const label=q(node,'[data-bk-scs4-sound-label]');if(label)label.textContent='Sound On'});
        qa(this.root,'[data-bk-scs4-pattern-preview]').forEach((node)=>{node.disabled=true;node.classList.remove('is-previewing')}); button?.classList.add('is-previewing'); if(button)button.textContent='■ Playing';
        const tempo=clamp(this.state.tempo||82,58,118),stepDuration=(60/tempo)/2,start=this.audioContext.currentTime+.06;
        pattern.forEach((velocity,index)=>{if(velocity>0)this.playInstrument(this.state.instrument||'keys',start+index*stepDuration,Math.max(.36,velocity),Math.max(44,this.state.energy||55),index,this.state.texture||'warm',this.state.space||45,1)});
        this.announce(`${this.titleCase(name)} pattern preview. This is the repeating timing of the beat.`);
        this.patternPreviewTimer=setTimeout(()=>{qa(this.root,'[data-bk-scs4-pattern-preview]').forEach((node)=>{node.disabled=false;node.classList.remove('is-previewing');node.textContent=`▶ Hear ${this.titleCase(node.dataset.bkScs4PatternPreview)}`});this.patternPreviewTimer=null},Math.ceil((pattern.length*stepDuration+.25)*1000));
      } catch(error){this.announce(error.message||'The pattern preview could not start.');}
    }

    themeTrackUrl(track = this.themeTrack) {
      return this.config.themeSong?.[track] || '';
    }

    ensureThemeAudio(track = this.themeTrack) {
      const url = this.themeTrackUrl(track);
      if (!url) throw new Error('This Bacci Kid song version does not have a URL yet.');
      if (!this.themeAudio || this.themeAudio.dataset.track !== track || this.themeAudio.src !== url) {
        this.stopThemeSong();
        this.themeAudio = new Audio(url);
        this.themeAudio.dataset.track = track;
        this.themeAudio.preload = 'metadata';
        this.themeAudio.volume = Number(q(this.root, '[data-bk-scs7-song-volume]')?.value || 72) / 100;
        this.themeAudio.addEventListener('ended', () => this.updateThemeSongStatus('Song finished. Choose Play to hear it again.'), { passive: true });
        this.themeAudio.addEventListener('error', () => this.updateThemeSongStatus('The song could not load. The Studio remains fully usable.'), { passive: true });
      }
      return this.themeAudio;
    }

    openSongPlayer() {
      this.selectThemeTrack(this.themeTrack || 'full', { announce: false });
      this.openDialog('song');
    }

    selectThemeTrack(track, options = {}) {
      this.themeTrack = ['full','instrumental','vocals'].includes(track) ? track : 'full';
      qa(this.root, '[data-bk-scs7-song-track]').forEach((button) => button.classList.toggle('is-active', button.dataset.bkScs7SongTrack === this.themeTrack));
      const names = { full: 'Full Song', instrumental: 'Instrumental', vocals: 'Vocals Only' };
      const name = q(this.root, '[data-bk-scs7-song-name]');
      if (name) name.textContent = names[this.themeTrack];
      this.stopThemeSong();
      this.updateThemeSongStatus('Ready when you are.');
      if (options.announce !== false) this.announce(`${names[this.themeTrack]} selected.`);
    }

    updateThemeSongStatus(message) {
      const status = q(this.root, '[data-bk-scs7-song-status]');
      if (status) status.textContent = message;
    }

    setThemeSongVolume(value) {
      const volume = clamp(Number(value || 72), 0, 100);
      const output = q(this.root, '[data-bk-scs7-song-volume-output]');
      if (output) output.textContent = `${volume}%`;
      if (this.themeAudio) this.themeAudio.volume = volume / 100;
    }

    async playThemeSong() {
      try {
        const audio = this.ensureThemeAudio(this.themeTrack);
        await audio.play();
        this.updateThemeSongStatus('Playing. You remain in control.');
        this.announce('Bacci Kid Theme Song is playing.');
      } catch (error) {
        this.updateThemeSongStatus(error.message || 'The song could not start.');
      }
    }

    pauseThemeSong() {
      if (!this.themeAudio) return;
      this.themeAudio.pause();
      this.updateThemeSongStatus('Paused.');
    }

    stopThemeSong() {
      if (!this.themeAudio) return;
      this.themeAudio.pause();
      try { this.themeAudio.currentTime = 0; } catch (_) {}
      this.updateThemeSongStatus('Stopped.');
    }

    async playSonicLogo() {
      const url = this.config.themeSong?.instrumental || this.config.themeSong?.full;
      if (!url) {
        this.announce('The Bacci Kid sonic logo is not connected yet.');
        return;
      }
      if (this.sonicTimer) window.clearTimeout(this.sonicTimer);
      try {
        if (this.themeAudio) this.stopThemeSong();
        const audio = new Audio(url);
        this.themeAudio = audio;
        audio.dataset.track = 'sonic';
        audio.preload = 'auto';
        audio.volume = .72;
        audio.currentTime = Number(this.config.themeSong?.sonicStart || 0);
        await audio.play();
        this.refs.welcome?.classList.add('is-sonic-playing');
        this.announce('Bacci Kid sonic glow is playing.');
        const duration = clamp(Number(this.config.themeSong?.sonicDuration || 5), 2, 8) * 1000;
        this.sonicTimer = window.setTimeout(() => {
          audio.pause();
          this.refs.welcome?.classList.remove('is-sonic-playing');
          this.sonicTimer = null;
        }, duration);
      } catch (_) {
        this.refs.welcome?.classList.remove('is-sonic-playing');
        this.announce('The sonic glow could not play. You can still enter the Studio.');
      }
    }

    async toggleSound() {
      try {
        await this.ensureAudio();
        this.soundEnabled = !this.soundEnabled;
        this.masterGain.gain.setTargetAtTime(this.soundEnabled ? this.outputLevel(this.state) : .0001, this.audioContext.currentTime, .05);
        qa(this.root, '[data-bk-scs4-sound]').forEach((button) => {
          button.setAttribute('aria-pressed', String(this.soundEnabled));
          button.setAttribute('aria-label', this.soundEnabled ? 'Turn Studio Sound off' : 'Turn Studio Sound on');
          const label = q(button, '[data-bk-scs4-sound-label]');
          if (label) label.textContent = this.soundEnabled ? 'Sound On' : 'Studio Sound';
        });
        this.announce(this.soundEnabled ? 'Studio sound is on.' : 'Studio sound is off.');
      } catch (error) { this.announce(error.message); }
    }

    async togglePlay(forcePlay = false) {
      if (this.isPlaying && !forcePlay) { this.stopMix(); return; }
      if (this.isPlaying && forcePlay) this.stopMix({ silent: true });
      try {
        await this.ensureAudio();
        this.videoSoundOn = false;
        if (this.refs.video) this.refs.video.muted = true;
        this.soundEnabled = true;
        this.isPlaying = true;
        this.root.classList.add('is-playing');
        this.masterGain.gain.setTargetAtTime(this.outputLevel(this.state), this.audioContext.currentTime, .05);
        this.step = 0;
        this.nextNoteTime = this.audioContext.currentTime + .08;
        this.startAmbient(this.state);
        this.scheduler();
        this.drawVisualizer();
        this.playVoice(this.state);
        this.updateTransport();
        this.updateMediaControls();
        this.announce('Your soundtrack is playing.');
      } catch (error) { this.announce(error.message || 'The soundtrack could not start.'); }
    }

    stopMix(options = {}) {
      if (this.schedulerTimer) window.clearTimeout(this.schedulerTimer);
      this.schedulerTimer = null;
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.isPlaying = false;
      this.root.classList.remove('is-playing');
      this.stopAmbient();
      if (this.masterGain && this.audioContext) this.masterGain.gain.setTargetAtTime(.0001, this.audioContext.currentTime, .045);
      this.root.style.setProperty('--bk-scs4-reactive', '0');
      this.updateTransport();
      this.drawIdleVisualizer();
      if (!options.silent) this.announce('Soundtrack stopped.');
    }

    updateTransport() {
      qa(this.root, '[data-bk-scs4-play]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.isPlaying));
        const label = q(button, '[data-bk-scs4-play-label]');
        if (label) label.textContent = this.isPlaying ? 'Pause My Mix' : 'Play My Mix';
        else if (button.closest('.bk-scs4__mobile-dock')) button.innerHTML = this.isPlaying ? '<span aria-hidden="true">⏸</span><em>Pause</em>' : '<span aria-hidden="true">▶</span><em>Play</em>';
      });
      qa(this.root, '[data-bk-scs4-stop]').forEach((button) => { button.disabled = !this.isPlaying; });
      const now = q(this.root, '[data-bk-scs4-now-playing]');
      if (now) now.textContent = this.isPlaying ? `${this.state.title} · ${this.titleCase(this.state.rhythm)} · ${this.titleCase(this.state.instrument)} · ${this.titleCase(this.state.ambient)}` : 'Ready when you are.';
    }

    scheduler() {
      if (!this.isPlaying || !this.audioContext) return;
      const secondsPerBeat = 60 / clamp(this.state.tempo, 48, 150);
      const stepDuration = secondsPerBeat / 2;
      while (this.nextNoteTime < this.audioContext.currentTime + .12) {
        const velocity = (PATTERNS[this.state.rhythm] || PATTERNS.heartbeat)[this.step % 8] || 0;
        if (velocity > 0) {
          this.playInstrument(this.state.instrument, this.nextNoteTime, velocity, this.state.energy, this.step, this.state.texture, this.state.space, 1);
          if (this.state.layerInstrument !== 'none' && this.state.layerLevel > 0) {
            this.playInstrument(this.state.layerInstrument, this.nextNoteTime + .012, velocity * (this.state.layerLevel / 100), this.state.energy, this.step, this.state.texture, this.state.space, this.harmonyRatio(this.state.harmony, this.step));
          }
        }
        this.step = (this.step + 1) % 8;
        this.nextNoteTime += stepDuration;
      }
      this.schedulerTimer = window.setTimeout(() => this.scheduler(), 25);
    }

    harmonyRatio(shape, step) {
      if (shape === 'same') return 1;
      if (shape === 'octave') return step % 2 ? 2 : .5;
      if (shape === 'third') return 1.25;
      if (shape === 'floating') return [1,.75,1.5,1.125][step % 4];
      return 1.5;
    }

    textureValues(texture) {
      const map = {
        warm: { release: 1.1, filter: 1400, bright: .45 }, airy: { release: 1.45, filter: 4200, bright: .75 },
        deep: { release: 1.3, filter: 850, bright: .25 }, glassy: { release: 1.65, filter: 6000, bright: .92 },
        cinematic: { release: 1.5, filter: 2600, bright: .68 }, raw: { release: .72, filter: 3600, bright: .58 }
      };
      return map[texture] || map.warm;
    }

    playInstrument(instrument, time, velocity, energy, step, texture = 'warm', space = 45, ratio = 1) {
      const gain = clamp((energy / 100) * velocity, .025, 1);
      const scale = [196,220,246.94,293.66,329.63,392,440,493.88];
      const base = scale[step % scale.length] * ratio;
      const tv = this.textureValues(texture);
      const pan = ((step % 8) / 7 - .5) * 2 * (space / 100) * .78;
      const release = tv.release;

      switch (instrument) {
        case 'bass':
          this.playTone(time, base / 2, 'sawtooth', .15 * gain, .02, .34 * release, 0, Math.min(1000, tv.filter), pan);
          return this.playTone(time, base / 4, 'sine', .08 * gain, .01, .42 * release, 0, 0, -pan * .3);
        case 'drums':
          if (step % 4 === 0) this.playKick(time, gain);
          else if (step % 4 === 2) this.playSnare(time, gain * .72);
          else this.playHat(time, gain * .42);
          return;
        case 'janggu':
          this.playJanggu(time, gain, step, pan); return;
        case 'chimes':
          this.playBell(time, base * 2, .09 * gain, .95 * release, pan); return;
        case 'glass-bells':
          this.playBell(time, base * 2.5, .095 * gain, 1.45 * release, pan);
          return this.playBell(time + .03, base * 3.01, .035 * gain, .95 * release, -pan);
        case 'synth':
          this.playTone(time, base, 'sawtooth', .055 * gain, .02, .48 * release, -8, tv.filter, pan);
          return this.playTone(time, base, 'sawtooth', .055 * gain, .02, .48 * release, 8, tv.filter, -pan);
        case 'strings':
          this.playTone(time, base, 'sawtooth', .045 * gain, .14, .78 * release, -5, Math.max(1000,tv.filter), pan);
          return this.playTone(time, base * 1.5, 'triangle', .03 * gain, .16, .84 * release, 5, 1800, -pan);
        case 'violin':
          this.playTone(time, base * 2, 'sawtooth', .04 * gain, .1, .72 * release, 4, Math.max(1800,tv.filter), pan);
          return this.playTone(time, base * 2.01, 'triangle', .026 * gain, .08, .68 * release, -5, 3100, -pan);
        case 'cello':
          this.playTone(time, base / 2, 'sawtooth', .055 * gain, .14, .9 * release, -4, Math.min(1450,tv.filter), pan);
          return this.playTone(time, base, 'triangle', .026 * gain, .12, .82 * release, 3, 1800, -pan);
        case 'harp':
          this.playPluck(time, base * 1.5, .12 * gain, .88 * release, pan, tv.bright);
          return this.playTone(time, base * 3, 'sine', .02 * gain, .004, .55 * release, 0, 0, -pan);
        case 'guitar':
          this.playPluck(time, base, .13 * gain, .58 * release, pan, .55);
          return this.playTone(time, base * 2, 'triangle', .018 * gain, .004, .32 * release, 0, 2300, -pan);
        case 'gayageum':
          this.playPluck(time, base * 1.25, .13 * gain, .75 * release, pan, .7);
          return this.playTone(time, base * 2.5, 'sine', .016 * gain, .004, .3 * release, 7, 0, -pan);
        case 'flute':
          return this.playFlute(time, base * 2, .07 * gain, .72 * release, pan, false);
        case 'daegeum':
          return this.playFlute(time, base, .075 * gain, .88 * release, pan, true);
        case 'kalimba':
          this.playTone(time, base * 1.5, 'sine', .095 * gain, .006, .58 * release, 0, 0, pan);
          return this.playTone(time, base * 4.03, 'sine', .028 * gain, .004, .34 * release, 0, 0, -pan);
        case 'handpan':
          this.playTone(time, base, 'sine', .095 * gain, .008, .9 * release, 0, 0, pan);
          this.playTone(time, base * 1.5, 'sine', .038 * gain, .008, .72 * release, 0, 0, -pan);
          return this.playTone(time, base * 2.01, 'triangle', .018 * gain, .006, .5 * release, 0, 0, pan * .5);
        case 'marimba':
          this.playTone(time, base, 'sine', .12 * gain, .004, .42 * release, 0, 0, pan);
          return this.playTone(time, base * 3.98, 'sine', .025 * gain, .003, .2 * release, 0, 0, -pan);
        default:
          this.playTone(time, base, 'triangle', .09 * gain, .02, .48 * release, 0, tv.filter, pan);
          return this.playTone(time, base * 2, 'sine', .03 * gain, .01, .35 * release, 0, 0, -pan);
      }
    }

    connectSource(node, pan = 0) {
      if (!this.audioContext || !this.mixBus) return;
      if (this.audioContext.createStereoPanner) {
        const panner = this.audioContext.createStereoPanner();
        panner.pan.value = clamp(pan, -1, 1);
        node.connect(panner).connect(this.mixBus);
      } else node.connect(this.mixBus);
    }

    playTone(time, frequency, type, level, attack, release, detune = 0, filterFrequency = 0, pan = 0) {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(Math.max(30, frequency), time);
      oscillator.detune.setValueAtTime(detune, time);
      gain.gain.setValueAtTime(.0001, time);
      gain.gain.exponentialRampToValueAtTime(Math.max(.0002, level), time + Math.max(.003, attack));
      gain.gain.exponentialRampToValueAtTime(.0001, time + Math.max(.003, attack) + Math.max(.06, release));
      if (filterFrequency) {
        const filter = this.audioContext.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(filterFrequency, time);
        oscillator.connect(filter).connect(gain);
      } else oscillator.connect(gain);
      this.connectSource(gain, pan);
      oscillator.start(time);
      oscillator.stop(time + attack + release + .08);
      return oscillator;
    }

    playFlute(time, frequency, level, release, pan, breathy) {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      const vibrato = this.audioContext.createOscillator();
      const vibratoGain = this.audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, time);
      vibrato.frequency.value = breathy ? 4.7 : 5.4;
      vibratoGain.gain.value = breathy ? 7 : 4;
      vibrato.connect(vibratoGain).connect(oscillator.frequency);
      gain.gain.setValueAtTime(.0001, time);
      gain.gain.exponentialRampToValueAtTime(Math.max(.0002, level), time + .08);
      gain.gain.exponentialRampToValueAtTime(.0001, time + .08 + release);
      oscillator.connect(gain);
      this.connectSource(gain, pan);
      oscillator.start(time); vibrato.start(time);
      oscillator.stop(time + release + .18); vibrato.stop(time + release + .18);
      if (breathy) this.playNoiseHit(time, .018 * level, release * .7, 1600, 5200, pan);
    }

    playPluck(time, frequency, level, release, pan = 0, brightness = .4) {
      const source = this.audioContext.createBufferSource();
      const filter = this.audioContext.createBiquadFilter();
      const gain = this.audioContext.createGain();
      source.buffer = this.makeNoiseBuffer(Math.max(.08, Math.min(.28, 3 / Math.max(80, frequency))));
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(frequency, time);
      filter.Q.value = 8 + brightness * 18;
      gain.gain.setValueAtTime(Math.max(.0002, level), time);
      gain.gain.exponentialRampToValueAtTime(.0001, time + release);
      source.connect(filter).connect(gain);
      this.connectSource(gain, pan);
      source.start(time); source.stop(time + release + .08);
    }

    playBell(time, frequency, level, release, pan = 0) {
      this.playTone(time, frequency, 'sine', level, .004, release, 0, 0, pan);
      this.playTone(time, frequency * 2.01, 'sine', level * .32, .003, release * .72, 0, 0, -pan);
      this.playTone(time, frequency * 3.96, 'sine', level * .12, .002, release * .48, 0, 0, pan * .5);
    }

    playKick(time, level) {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      oscillator.frequency.setValueAtTime(150, time);
      oscillator.frequency.exponentialRampToValueAtTime(46, time + .18);
      gain.gain.setValueAtTime(.18 * level, time);
      gain.gain.exponentialRampToValueAtTime(.0001, time + .24);
      oscillator.connect(gain); this.connectSource(gain, 0);
      oscillator.start(time); oscillator.stop(time + .25);
    }

    playSnare(time, level) { this.playNoiseHit(time, .08 * level, .17, 900, 7800, 0); }
    playHat(time, level) { this.playNoiseHit(time, .035 * level, .07, 5200, 12000, .3); }

    playJanggu(time, level, step, pan) {
      if (step % 2 === 0) {
        this.playTone(time, 150, 'sine', .12 * level, .005, .22, 0, 650, pan);
        this.playNoiseHit(time, .035 * level, .12, 600, 2600, pan);
      } else {
        this.playTone(time, 230, 'triangle', .07 * level, .004, .14, 0, 1200, -pan);
        this.playNoiseHit(time, .026 * level, .08, 1800, 7000, -pan);
      }
    }

    playNoiseHit(time, level, release, low = 500, high = 9000, pan = 0) {
      const source = this.audioContext.createBufferSource();
      const highpass = this.audioContext.createBiquadFilter();
      const lowpass = this.audioContext.createBiquadFilter();
      const gain = this.audioContext.createGain();
      source.buffer = this.makeNoiseBuffer(Math.max(.1, release + .04));
      highpass.type = 'highpass'; highpass.frequency.value = low;
      lowpass.type = 'lowpass'; lowpass.frequency.value = high;
      gain.gain.setValueAtTime(Math.max(.0002, level), time);
      gain.gain.exponentialRampToValueAtTime(.0001, time + release);
      source.connect(highpass).connect(lowpass).connect(gain);
      this.connectSource(gain, pan);
      source.start(time); source.stop(time + release + .04);
    }

    makeNoiseBuffer(seconds = 2) {
      const key = Math.round(seconds * 100) / 100;
      if (this.noiseCache.has(key)) return this.noiseCache.get(key);
      const length = Math.max(1, Math.floor(this.audioContext.sampleRate * seconds));
      const buffer = this.audioContext.createBuffer(1, length, this.audioContext.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
      this.noiseCache.set(key, buffer);
      return buffer;
    }

    startAmbient(state = this.state) {
      this.stopAmbient();
      if (!this.audioContext || !this.mixBus || state.ambient === 'quiet' || state.ambientLevel <= 0) return;
      const source = this.audioContext.createBufferSource();
      source.buffer = this.makeNoiseBuffer(4);
      source.loop = true;
      const filterA = this.audioContext.createBiquadFilter();
      const filterB = this.audioContext.createBiquadFilter();
      const gain = this.audioContext.createGain();
      const level = (state.ambientLevel / 100) * .06;
      const settings = {
        rain: ['highpass', 1600, 'lowpass', 8200, level],
        ocean: ['lowpass', 720, 'highpass', 55, level * 1.35],
        wind: ['bandpass', 620, 'lowpass', 2400, level],
        night: ['bandpass', 3200, 'highpass', 1200, level * .3],
        forest: ['bandpass', 1900, 'lowpass', 5200, level * .48],
        city: ['bandpass', 860, 'lowpass', 4100, level * .7],
        space: ['bandpass', 430, 'lowpass', 1800, level * .62],
        fire: ['bandpass', 1200, 'highpass', 280, level * .46],
        crystal: ['highpass', 3100, 'lowpass', 9200, level * .24]
      }[state.ambient] || ['lowpass', 1200, 'highpass', 80, level * .45];
      filterA.type = settings[0]; filterA.frequency.value = settings[1];
      filterB.type = settings[2]; filterB.frequency.value = settings[3];
      gain.gain.value = settings[4];
      source.connect(filterA).connect(filterB).connect(gain); this.connectSource(gain, 0);
      source.start();
      this.ambientNodes.push(source, filterA, filterB, gain);

      if (['ocean','wind','space','fire'].includes(state.ambient)) {
        const lfo = this.audioContext.createOscillator();
        const lfoGain = this.audioContext.createGain();
        lfo.frequency.value = state.ambient === 'fire' ? 2.7 : state.ambient === 'ocean' ? .11 : .17;
        lfoGain.gain.value = settings[4] * .7;
        lfo.connect(lfoGain).connect(gain.gain);
        lfo.start(); this.ambientNodes.push(lfo, lfoGain);
      }
      if (state.ambient === 'crystal') {
        const now = this.audioContext.currentTime;
        [0,.8,1.9,2.8].forEach((offset, index) => this.playBell(now + offset, [523.25,659.25,783.99,1046.5][index], .018, 1.2, index % 2 ? .6 : -.6));
      }
    }

    restartAmbient() { if (this.isPlaying) this.startAmbient(this.state); }
    stopAmbient() {
      this.ambientNodes.forEach((node) => { try { node.stop?.(); } catch (_) {} try { node.disconnect?.(); } catch (_) {} });
      this.ambientNodes = [];
    }

    playVoice(state = this.state) {
      if (!state.phrase) return;
      if (state.voice === 'device' && this.config.enableDeviceVoice && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(state.phrase);
        utterance.rate = .9; utterance.pitch = 1.03; utterance.volume = .82;
        window.speechSynthesis.speak(utterance);
      } else if (state.voice === 'mine' && this.recordedVoiceUrl) {
        const audio = new Audio(this.recordedVoiceUrl); audio.volume = .82; audio.play().catch(() => {});
      }
    }

    async triggerBeatPad(index, button) {
      try {
        await this.ensureAudio();
        this.soundEnabled = true;
        this.masterGain.gain.setTargetAtTime(this.outputLevel(this.state), this.audioContext.currentTime, .02);
        const time = this.audioContext.currentTime + .01;
        this.playInstrument(this.state.instrument, time, .72 + index * .04, this.state.energy, index, this.state.texture, this.state.space, 1);
        if (this.state.layerInstrument !== 'none' && this.state.layerLevel > 0) this.playInstrument(this.state.layerInstrument, time + .012, (.56 + index * .028) * (this.state.layerLevel / 100), this.state.energy, index, this.state.texture, this.state.space, this.harmonyRatio(this.state.harmony, index));
        button?.classList.remove('is-hit'); void button?.offsetWidth; button?.classList.add('is-hit');
        window.setTimeout(() => button?.classList.remove('is-hit'), 260);
      } catch (error) { this.announce(error.message || 'The sound pad could not start.'); }
    }

    filterInstruments(filter) {
      qa(this.root, '[data-bk-scs4-instrument-filter]').forEach((button) => button.classList.toggle('is-active', button.dataset.bkScs4InstrumentFilter === filter));
      qa(this.root, '[data-instrument-family]').forEach((label) => { label.hidden = filter !== 'all' && label.dataset.instrumentFamily !== filter; });
    }

    /* ----------------------------------------------------------------------
       Canvas visual systems
       ---------------------------------------------------------------------- */

    resizeCanvas(canvas) {
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      return { context: canvas.getContext('2d'), width, height, dpr };
    }

    resizeCanvases() {
      this.drawSequencer(); this.drawMixCanvas(); this.drawCardWave();
      if (!this.isPlaying) this.drawIdleVisualizer();
    }

    drawSequencer() {
      const data = this.resizeCanvas(this.refs.sequencer);
      if (!data) return;
      const { context: ctx, width, height } = data;
      ctx.clearRect(0,0,width,height);
      const pattern = PATTERNS[this.state.rhythm] || PATTERNS.heartbeat;
      const gap = width * .015;
      const cell = (width - gap * 9) / 8;
      const gradient = ctx.createLinearGradient(0,0,width,0);
      gradient.addColorStop(0,this.state.color); gradient.addColorStop(.5,'#45dfff'); gradient.addColorStop(1,'#ffd56f');
      pattern.forEach((velocity,index) => {
        const x = gap + index * (cell + gap);
        const h = height * (.18 + velocity * .62);
        const y = height - h - height * .12;
        ctx.fillStyle = velocity ? gradient : 'rgba(255,255,255,.08)';
        this.roundRect(ctx,x,y,cell,h,Math.min(18,cell*.18)); ctx.fill();
      });
      ctx.strokeStyle='rgba(255,255,255,.18)';ctx.lineWidth=Math.max(1,width/900);ctx.beginPath();ctx.moveTo(gap,height*.88);ctx.lineTo(width-gap,height*.88);ctx.stroke();
    }

    drawMixCanvas() {
      const data = this.resizeCanvas(this.refs.mixCanvas);
      if (!data) return;
      const { context: ctx, width, height } = data;
      ctx.clearRect(0,0,width,height);
      const layers = [
        ['COLOR',this.state.color],['RHYTHM','#a060ff'],[this.titleCase(this.state.instrument),'#45dfff'],
        [this.state.layerInstrument !== 'none' ? this.titleCase(this.state.layerInstrument) : 'SECOND LAYER','rgba(255,255,255,.18)'],
        [this.titleCase(this.state.ambient),'#ff7dbd'],['ENERGY','#ffd56f']
      ];
      const rowH = height / layers.length;
      layers.forEach(([label,color],row) => {
        const y = rowH * row + rowH/2;
        ctx.fillStyle='rgba(255,255,255,.72)';ctx.font=`700 ${Math.max(10,width/70)}px system-ui`;ctx.fillText(label,width*.03,y+4);
        const start=width*.24; const end=width*.97; const mid=(start+end)/2;
        ctx.strokeStyle=color;ctx.lineWidth=Math.max(2,width/500);ctx.shadowColor=color;ctx.shadowBlur=12;ctx.beginPath();
        for(let x=start;x<=end;x+=Math.max(5,width/220)){
          const amplitude=row===5?this.state.energy/100:.45+row*.055;
          const wave=Math.sin(x*.018+row*1.4)*rowH*.18*amplitude+Math.sin(x*.043-row)*rowH*.07;
          if(x===start)ctx.moveTo(x,y+wave);else ctx.lineTo(x,y+wave);
        }
        ctx.stroke();ctx.shadowBlur=0;
        ctx.fillStyle='rgba(255,255,255,.08)';ctx.fillRect(mid-1,y-rowH*.32,2,rowH*.64);
      });
    }

    drawIdleVisualizer() {
      const data = this.resizeCanvas(this.refs.visualizer);
      if (!data) return;
      const { context: ctx, width, height } = data;
      ctx.clearRect(0,0,width,height);
      const gradient=ctx.createLinearGradient(0,0,width,0);gradient.addColorStop(0,this.state.color);gradient.addColorStop(.5,'#45dfff');gradient.addColorStop(1,'#ffd56f');
      ctx.strokeStyle=gradient;ctx.lineWidth=Math.max(2,width/520);ctx.shadowColor=this.state.color;ctx.shadowBlur=20;ctx.beginPath();
      for(let x=0;x<=width;x+=Math.max(5,width/260)){
        const y=height*.56+Math.sin(x*.012)*height*.08+Math.sin(x*.031)*height*.025;
        if(x===0)ctx.moveTo(x,y);else ctx.lineTo(x,y);
      }
      ctx.stroke();ctx.shadowBlur=0;
    }

    drawVisualizer() {
      if (!this.isPlaying || !this.analyser) return;
      const data = this.resizeCanvas(this.refs.visualizer);
      if (!data) return;
      const { context: ctx, width, height } = data;
      const bins = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(bins);
      const average = bins.reduce((sum,value)=>sum+value,0)/(bins.length*255);
      this.root.style.setProperty('--bk-scs4-reactive',average.toFixed(3));
      ctx.clearRect(0,0,width,height);
      const barCount=48;const gap=width*.004;const barW=(width-gap*(barCount+1))/barCount;
      const gradient=ctx.createLinearGradient(0,0,width,0);gradient.addColorStop(0,this.state.color);gradient.addColorStop(.5,'#45dfff');gradient.addColorStop(1,'#ffd56f');
      for(let i=0;i<barCount;i+=1){
        const value=bins[Math.floor(i*(bins.length/barCount))]/255;
        const h=Math.max(height*.035,value*height*.68);const x=gap+i*(barW+gap);const y=height-h;
        ctx.fillStyle=gradient;ctx.shadowColor=i%2?this.state.color:'#45dfff';ctx.shadowBlur=14;this.roundRect(ctx,x,y,barW,h,barW*.4);ctx.fill();
      }
      ctx.shadowBlur=0;
      this.animationFrame=window.requestAnimationFrame(()=>this.drawVisualizer());
    }

    drawCardWave() {
      const data = this.resizeCanvas(this.refs.cardWave);
      if (!data) return;
      const { context:ctx,width,height }=data;ctx.clearRect(0,0,width,height);
      const gradient=ctx.createLinearGradient(0,0,width,0);gradient.addColorStop(0,this.state.color);gradient.addColorStop(.5,'#45dfff');gradient.addColorStop(1,'#ffd56f');
      ctx.strokeStyle=gradient;ctx.lineWidth=Math.max(2,width/420);ctx.shadowColor=this.state.color;ctx.shadowBlur=14;ctx.beginPath();
      for(let x=0;x<=width;x+=Math.max(4,width/260)){const y=height*.5+Math.sin(x*.026)*height*.22+Math.sin(x*.071)*height*.1;if(x===0)ctx.moveTo(x,y);else ctx.lineTo(x,y)}ctx.stroke();ctx.shadowBlur=0;
    }

    startParticles() {
      const canvas=this.refs.particles;if(!canvas||this.reduced()||this.saveData)return;
      const ctx=canvas.getContext('2d');let particles=[];let width=0;let height=0;let last=0;
      const resize=()=>{const rect=canvas.getBoundingClientRect();const dpr=Math.min(window.devicePixelRatio||1,1.5);width=canvas.width=Math.max(1,Math.round(rect.width*dpr));height=canvas.height=Math.max(1,Math.round(rect.height*dpr));particles=Array.from({length:Math.min(52,Math.floor(width/35))},()=>({x:Math.random()*width,y:Math.random()*height,r:.7+Math.random()*2.2,v:.08+Math.random()*.25,a:.2+Math.random()*.65,p:Math.random()*Math.PI*2}))};
      resize();this.bind(window,'resize',resize,{passive:true});
      const loop=(time)=>{if(document.hidden||this.root.classList.contains('is-calm-mode')||this.reduced()){this.particleFrame=requestAnimationFrame(loop);return}if(time-last<32){this.particleFrame=requestAnimationFrame(loop);return}last=time;ctx.clearRect(0,0,width,height);particles.forEach((particle,index)=>{particle.y-=particle.v;particle.x+=Math.sin(time*.0004+particle.p)*.12;if(particle.y<-10){particle.y=height+10;particle.x=Math.random()*width}ctx.beginPath();ctx.fillStyle=index%3===0?`rgba(69,223,255,${particle.a})`:index%3===1?`rgba(255,79,163,${particle.a})`:`rgba(255,213,111,${particle.a})`;ctx.shadowBlur=10;ctx.shadowColor=ctx.fillStyle;ctx.arc(particle.x,particle.y,particle.r,0,Math.PI*2);ctx.fill()});ctx.shadowBlur=0;this.particleFrame=requestAnimationFrame(loop)};
      this.particleFrame=requestAnimationFrame(loop);
    }

    /* ----------------------------------------------------------------------
       Breathing and check-in
       ---------------------------------------------------------------------- */

    openDialog(type) {
      const selectors = { song:'[data-bk-scs7-song-dialog]', guide:'[data-bk-scs4-guide-dialog]', checkin:'[data-bk-scs4-checkin-dialog]', breathing:'[data-bk-scs4-breathing-dialog]', trust:'[data-bk-scs4-trust-dialog]', library:'[data-bk-scs4-library-dialog]', realm:'[data-bk-scs4-realm-dialog]', completion:'[data-bk-scs4-completion-dialog]' };
      const dialog=q(this.root,selectors[type]);if(!dialog)return;
      if(typeof dialog.showModal==='function'){if(!dialog.open)dialog.showModal()}else dialog.setAttribute('open','');
    }

    closeDialog(type) {
      const selectors={checkin:'[data-bk-scs4-checkin-dialog]',breathing:'[data-bk-scs4-breathing-dialog]',trust:'[data-bk-scs4-trust-dialog]',library:'[data-bk-scs4-library-dialog]',realm:'[data-bk-scs4-realm-dialog]',completion:'[data-bk-scs4-completion-dialog]'};
      const dialog=q(this.root,selectors[type]);if(!dialog)return;if(typeof dialog.close==='function')dialog.close();else dialog.removeAttribute('open');
    }

    handleCheckIn(action) {
      const response=q(this.root,'[data-bk-scs4-checkin-response]');
      const messages={
        continue:'You can return exactly as you are. Nothing has to be solved before you create.',
        quiet:'Calm Mode is on. Decorative motion is reduced and the studio sound is softened.',
        encourage:'You matter. Your pace matters. Quiet courage, uncertainty, rest, and asking for help can all belong here.',
        trusted:'Reaching out can be a strong next step. Tell, call, or message a trusted adult or another person you trust.',
        unsure:'Not knowing is a real answer. You may stay here, choose a quiet minute, or return to your mix.'
      };
      if(action==='continue'){this.closeDialog('checkin');this.announce('Welcome back to the studio.');return}
      if(action==='quiet'){if(!this.root.classList.contains('is-calm-mode'))this.toggleCalm();}
      if(action==='breathe'){this.closeDialog('checkin');this.openBreathing();return}
      if(response)response.textContent=messages[action]||messages.unsure;
    }

    checkDirectPhrase(text) {
      if(!this.config.enableDirectPhraseCheckIn||this.phrasePromptShown)return;
      const normalized=String(text||'').toLowerCase();
      const explicit=[/\bi want to die\b/,/\bkill myself\b/,/\bend my life\b/,/\bhurt myself\b/,/\bself[- ]?harm\b/,/\bi am not safe\b/,/\bsomeone is hurting me\b/];
      if(!explicit.some((pattern)=>pattern.test(normalized)))return;
      this.phrasePromptShown=true;
      this.openDialog('checkin');
      const response=q(this.root,'[data-bk-scs4-checkin-response]');
      if(response)response.textContent='This sounds important and deserves real-world support now. Please tell a trusted adult immediately. This creative studio cannot monitor emergencies or contact help for you.';
    }

    openBreathing() {
      this.wasPlayingBeforeBreath=this.isPlaying;
      if(this.isPlaying)this.stopMix({silent:true});
      this.pauseSceneVideo({preservePoster:false});
      this.setBreathingMode('quiet');this.setBreathCount(1);this.openDialog('breathing');
    }

    setBreathingMode(mode) {
      const allowed=['quiet','world','music'];
      const next=allowed.includes(mode)?mode:'quiet';
      if(next==='music'&&!this.config.breathing?.musicEnabled){this.announce('Breathing with music is not enabled until the soundtrack rights are confirmed.');return;}
      this.breathingMode=next;
      this.breathSoundOn=next!=='quiet';
      qa(this.root,'[data-bk-scs4-breathing-mode]').forEach((button)=>{
        const active=button.dataset.bkScs4BreathingMode===next;
        button.classList.toggle('is-active',active);
        button.setAttribute('aria-pressed',String(active));
      });
      this.cleanupBreathingMedia();
      const reduced=this.reduced();
      if(this.refs.breathPoster)this.refs.breathPoster.src=reduced?(this.config.breathing?.reducedMotionPoster||this.config.breathing?.poster):(this.config.breathing?.poster||'');
      if(this.refs.breathVideo&&!reduced){
        this.refs.breathVideo.src=next==='music'?this.config.breathing?.musicVideo:this.config.breathing?.silentVideo;
        this.refs.breathVideo.muted=next!=='music'||!this.breathSoundOn;
        this.refs.breathVideo.load();
      }
      this.updateBreathingSoundButton();
      this.announce(next==='world'?'Your selected ambient world will accompany the silent breathing film.':next==='music'?'Breathing with music selected.':'Quiet breathing selected.');
    }

    setBreathCount(count) {
      this.breathTarget=Number.isFinite(count)?count:1;this.breathCompleted=0;
      qa(this.root,'[data-bk-scs4-breath-count]').forEach((button)=>button.classList.toggle('is-active',Number(button.dataset.bkScs4BreathCount)===this.breathTarget));
    }

    async startBreathing() {
      this.breathingActive=true;this.breathCompleted=0;
      this.refs.breathMedia?.classList.add('is-breathing');
      if(this.refs.breathVideo&&!this.reduced()){
        this.refs.breathMedia?.classList.add('is-video');
        try{await this.refs.breathVideo.play()}catch(_){this.refs.breathMedia?.classList.remove('is-video')}
      }
      if(this.breathingMode==='world'){
        try{await this.ensureAudio();this.soundEnabled=this.breathSoundOn;this.masterGain.gain.setTargetAtTime(this.breathSoundOn?.055:.0001,this.audioContext.currentTime,.08);this.startAmbient({...this.state,energy:25,ambientLevel:Math.min(45,this.state.ambientLevel)})}catch(_){ }
      }
      if(this.refs.breathCue)this.refs.breathCue.textContent='Breathe in gently.';
      window.clearInterval(this.breathTimer);
      let phase='in';
      this.breathTimer=window.setInterval(()=>{
        if(!this.breathingActive)return;
        if(phase==='in'){phase='out';if(this.refs.breathCue)this.refs.breathCue.textContent='Breathe out slowly. Let this breath make a little more room.'}
        else{phase='in';this.breathCompleted+=1;if(this.breathTarget>0&&this.breathCompleted>=this.breathTarget){this.pauseBreathing();if(this.refs.breathCue)this.refs.breathCue.textContent='That breath counts. Continue only if you want.';return}if(this.refs.breathCue)this.refs.breathCue.textContent='Breathe in gently.'}
      },5000);
      this.announce('Breathing moment started. Stop whenever you need.');
    }

    pauseBreathing() {
      this.breathingActive=false;window.clearInterval(this.breathTimer);this.breathTimer=null;
      this.refs.breathMedia?.classList.remove('is-breathing');this.refs.breathVideo?.pause();this.stopAmbient();
      if(this.refs.breathCue)this.refs.breathCue.textContent='Paused. Your pace is allowed.';
    }

    toggleBreathingSound() {
      if(this.breathingMode==='quiet'){
        this.breathSoundOn=false;
        this.updateBreathingSoundButton();
        this.announce('Quiet Breathing stays silent. Choose Use My Sound World for gentle ambience.');
        return;
      }
      this.breathSoundOn=!this.breathSoundOn;
      if(this.breathingMode==='music'&&this.refs.breathVideo)this.refs.breathVideo.muted=!this.breathSoundOn;
      if(this.breathingMode==='world'&&this.masterGain&&this.audioContext)this.masterGain.gain.setTargetAtTime(this.breathSoundOn?.055:.0001,this.audioContext.currentTime,.08);
      this.updateBreathingSoundButton();
      this.announce(this.breathSoundOn?'Breathing sound is on.':'Breathing sound is off.');
    }

    updateBreathingSoundButton() {
      const button=q(this.root,'[data-bk-scs4-breath-sound]');
      if(!button)return;
      const quiet=this.breathingMode==='quiet';
      button.disabled=quiet;
      button.setAttribute('aria-pressed',String(!quiet&&this.breathSoundOn));
      button.textContent=quiet?'Silent':this.breathSoundOn?'Sound On':'Sound Off';
      button.setAttribute('aria-label',quiet?'Quiet Breathing has no sound':this.breathSoundOn?'Turn breathing sound off':'Turn breathing sound on');
    }

    endBreathing() { this.pauseBreathing();this.closeDialog('breathing');this.announce('You are back in the studio.'); }
    cleanupBreathingMedia(){this.refs.breathVideo?.pause();if(this.refs.breathVideo)this.refs.breathVideo.removeAttribute('src');this.refs.breathVideo?.load();this.refs.breathMedia?.classList.remove('is-video','is-breathing');this.stopAmbient()}
    cleanupBreathing(){this.pauseBreathing();this.cleanupBreathingMedia()}

    /* ----------------------------------------------------------------------
       Realms, cards, library, completion
       ---------------------------------------------------------------------- */

    renderRealms() {
      const rail=q(this.root,'[data-bk-scs4-realm-rail]');if(!rail)return;
      const visible=this.realms.filter((realm)=>this.realmFilter==='all'||realm.category===this.realmFilter);
      rail.innerHTML=visible.map((realm)=>`<article class="bk-scs4__realm-card" data-realm-id="${escapeHtml(realm.id)}" tabindex="0" aria-label="Open ${escapeHtml(realm.title)} realm"><img src="${escapeHtml(realm.image)}" alt="${escapeHtml(realm.title)} Bacci Kid inspiration realm" loading="lazy" decoding="async"><div class="bk-scs4__realm-card-content"><small>${escapeHtml(realm.category)}</small><button type="button" aria-label="Open ${escapeHtml(realm.title)}">Enter Realm</button></div></article>`).join('');
      qa(rail,'[data-realm-id]').forEach((card)=>{
        const open=()=>this.openRealm(card.dataset.realmId);
        this.bind(card,'click',open);this.bind(card,'keydown',(event)=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();open()}});
      });
    }

    filterRealms(filter) { this.realmFilter=filter||'all';qa(this.root,'[data-bk-scs4-realm-filter]').forEach((button)=>button.classList.toggle('is-active',button.dataset.bkScs4RealmFilter===this.realmFilter));this.renderRealms(); }
    scrollToRealms(){q(this.root,'[data-bk-scs4-realms]')?.scrollIntoView({behavior:this.reduced()?'auto':'smooth',block:'start'})}
    scrollToConsole(){q(this.root,'.bk-scs4__console')?.scrollIntoView({behavior:this.reduced()?'auto':'smooth',block:'start'})}
    scrollToTheater(){this.refs.theater?.scrollIntoView({behavior:this.reduced()?'auto':'smooth',block:'start'})}

    openRealm(id) {
      const realm=this.realms.find((item)=>item.id===id);if(!realm)return;this.activeRealm=realm;
      const image=q(this.root,'[data-bk-scs4-realm-image]');const title=q(this.root,'[data-bk-scs4-realm-title]');const category=q(this.root,'[data-bk-scs4-realm-category]');const note=q(this.root,'[data-bk-scs4-realm-note]');
      if(image){image.src=realm.image;image.alt=`${realm.title} Bacci Kid affirmation realm`;}if(title)title.textContent=realm.title;if(category)category.textContent=realm.category;if(note)note.textContent=realm.note;
      this.openDialog('realm');
    }

    loadActiveRealm(){if(!this.activeRealm)return;this.loadPreset(this.activeRealm.preset,{navigate:true});this.closeDialog('realm');}

    updateCardPreview(state=this.state) {
      const title=q(this.root,'[data-bk-scs4-card-title]');const details=q(this.root,'[data-bk-scs4-card-details]');
      if(title)title.textContent=state.title;
      if(details)details.textContent=`${state.colorName} · ${this.titleCase(state.rhythm)} · ${this.titleCase(state.instrument)}${state.layerInstrument!=='none'?` + ${this.titleCase(state.layerInstrument)}`:''} · ${this.titleCase(state.ambient)}`;
      this.drawCardWave();
    }

    drawSoundCard(state=this.state) {
      const canvas=this.refs.cardCanvas;const ctx=canvas?.getContext('2d');if(!canvas||!ctx)return null;
      const width=canvas.width,height=canvas.height,secondary=this.mixHex(state.color,'#45dfff',.46),gold='#ffd56f';
      const background=ctx.createLinearGradient(0,0,width,height);background.addColorStop(0,'#08041e');background.addColorStop(.45,this.mixHex(state.color,'#13072d',.72));background.addColorStop(1,'#061b38');ctx.fillStyle=background;ctx.fillRect(0,0,width,height);
      this.drawGlow(ctx,width*.18,height*.2,height*.52,state.color,.52);this.drawGlow(ctx,width*.84,height*.27,height*.46,secondary,.43);this.drawGlow(ctx,width*.66,height*.88,height*.36,gold,.22);
      ctx.strokeStyle='rgba(255,255,255,.27)';ctx.lineWidth=6;this.roundRect(ctx,80,80,width-160,height-160,80);ctx.stroke();
      ctx.fillStyle='#fff';ctx.font='800 92px system-ui,sans-serif';ctx.fillText(this.config.cardBrand||'Bacci Kid',180,210);ctx.font='700 38px system-ui,sans-serif';ctx.fillStyle='rgba(255,255,255,.72)';ctx.fillText(this.config.cardHub||'BRAVE · TEEN HUB',185,270);
      ctx.font='900 210px system-ui,sans-serif';ctx.fillStyle='#fff';this.wrapText(ctx,state.title,180,620,width*.63,225);
      ctx.font='700 56px system-ui,sans-serif';ctx.fillStyle=secondary;ctx.fillText(`${state.colorName} · ${this.titleCase(state.rhythm)} · ${this.titleCase(state.instrument)}${state.layerInstrument!=='none'?` + ${this.titleCase(state.layerInstrument)}`:''}`,190,1030);ctx.fillStyle='rgba(255,255,255,.8)';ctx.fillText(`${this.titleCase(state.ambient)} · ${state.tempo} BPM · ${state.energy}% energy · ${state.energyWord}`,190,1110);
      const orbX=width*.78,orbY=height*.55,orbRadius=390;const orbGradient=ctx.createRadialGradient(orbX-110,orbY-120,20,orbX,orbY,orbRadius);orbGradient.addColorStop(0,'#fff');orbGradient.addColorStop(.15,secondary);orbGradient.addColorStop(.62,state.color);orbGradient.addColorStop(1,'#17082e');ctx.fillStyle=orbGradient;ctx.shadowColor=state.color;ctx.shadowBlur=110;ctx.beginPath();ctx.arc(orbX,orbY,orbRadius,0,Math.PI*2);ctx.fill();ctx.shadowBlur=0;ctx.strokeStyle='rgba(255,255,255,.48)';ctx.lineWidth=12;ctx.stroke();ctx.fillStyle='#fff';ctx.font='900 220px system-ui,sans-serif';ctx.textAlign='center';ctx.fillText('♥',orbX,orbY+75);ctx.textAlign='left';
      const waveY=height*.72,waveGradient=ctx.createLinearGradient(170,0,width-170,0);waveGradient.addColorStop(0,state.color);waveGradient.addColorStop(.5,secondary);waveGradient.addColorStop(1,gold);ctx.strokeStyle=waveGradient;ctx.lineWidth=15;ctx.shadowColor=secondary;ctx.shadowBlur=24;ctx.beginPath();for(let x=170;x<=width-170;x+=12){const y=waveY+Math.sin(x*.022)*80+Math.sin(x*.063)*40;if(x===170)ctx.moveTo(x,y);else ctx.lineTo(x,y)}ctx.stroke();ctx.shadowBlur=0;
      if(state.includePhrase&&state.phrase){ctx.font='650 62px system-ui,sans-serif';ctx.fillStyle='rgba(255,255,255,.9)';this.wrapText(ctx,`“${state.phrase}”`,190,height-360,width-380,78)}
      ctx.fillStyle='rgba(255,255,255,.66)';ctx.font='700 38px system-ui,sans-serif';ctx.fillText('YOUR FEELINGS · YOUR COLORS · YOUR SOUNDS · YOUR WAY',190,height-150);ctx.textAlign='right';ctx.fillText('PRIVATE BY DEFAULT',width-190,height-150);ctx.textAlign='left';return canvas;
    }

    async downloadSoundCard(stateOverride) {
      if(!this.config.enableDownload)return;const state=stateOverride||this.state;const canvas=this.drawSoundCard(state);if(!canvas)return;
      canvas.toBlob((blob)=>{if(!blob){this.announce('The visual card could not be created in this browser.');return}const url=URL.createObjectURL(blob);const anchor=document.createElement('a');anchor.href=url;anchor.download=`${this.slugify(state.title||'bacci-kid-sound-card')}.png`;document.body.appendChild(anchor);anchor.click();anchor.remove();setTimeout(()=>URL.revokeObjectURL(url),2200);this.announce('Your 3840 × 2160 visual sound card is ready.');this.openCompletion();},'image/png');
    }

    persistState() {
      try{const safe={...this.state,phrase:this.state.includePhrase?this.state.phrase:'',voice:'silent'};localStorage.setItem(`${this.config.storageKey}:last`,JSON.stringify(safe));localStorage.setItem(`${this.config.storageKey}:calm`,this.root.classList.contains('is-calm-mode')?'1':'0')}catch(_){ }
    }

    restoreState() {
      try{const saved=JSON.parse(localStorage.getItem(`${this.config.storageKey}:last`)||'null');if(saved)this.state={...DEFAULT_STATE,...saved};if(localStorage.getItem(`${this.config.storageKey}:calm`)==='1')this.root.classList.add('is-calm-mode')}catch(_){this.state={...DEFAULT_STATE}}
      this.updateCalmButtons();
    }

    saveToLibrary() {
      const state={...this.state,phrase:this.state.includePhrase?this.state.phrase:'',voice:'silent',savedAt:new Date().toISOString(),id:`${Date.now()}-${Math.random().toString(36).slice(2,7)}`};
      const cards=this.getLibrary();cards.unshift(state);try{localStorage.setItem(`${this.config.storageKey}:cards`,JSON.stringify(cards.slice(0,24)));this.announce('Mix saved to My Cards on this browser.')}catch(_){this.announce('This browser could not save the mix.')}this.renderLibrary();
    }
    getLibrary(){try{return JSON.parse(localStorage.getItem(`${this.config.storageKey}:cards`)||'[]')}catch(_){return[]}}
    openLibrary(){this.renderLibrary();this.openDialog('library')}
    renderLibrary(){const container=q(this.root,'[data-bk-scs4-library]');if(!container)return;const cards=this.getLibrary();if(!cards.length){container.innerHTML='<p>No saved cards yet. Create a mix and choose “Save Choices to My Cards.”</p>';return}container.innerHTML='';cards.forEach((card)=>{const article=document.createElement('article');article.className='bk-scs4__library-card';article.innerHTML=`<strong>${escapeHtml(card.title||'Saved mix')}</strong><p>${escapeHtml(card.colorName||'Color')} · ${escapeHtml(this.titleCase(card.rhythm||''))} · ${escapeHtml(this.titleCase(card.instrument||''))}</p><small>${card.savedAt?new Date(card.savedAt).toLocaleDateString():''}</small>`;const button=document.createElement('button');button.type='button';button.textContent='Open mix';button.addEventListener('click',()=>{this.applyState(card,{restorePhrase:Boolean(card.includePhrase)});this.closeDialog('library');this.setMode('pro',{announce:false});this.setTool('vault',{announce:false});this.goToScene(9,{focus:false});this.scrollToConsole();this.announce(`${card.title||'Saved mix'} loaded.`)},{once:true});article.appendChild(button);container.appendChild(article)})}
    clearLibrary(){try{localStorage.removeItem(`${this.config.storageKey}:cards`)}catch(_){ }this.renderLibrary();this.announce('My Cards was cleared from this browser.')}

    openCompletion(){this.openDialog('completion');this.playPhoenix()}
    playPhoenix(){if(!this.refs.phoenixVideo||!this.config.phoenixVideoUrl)return;this.refs.phoenixVideo.src=this.config.phoenixVideoUrl;this.refs.phoenixVideo.muted=true;this.refs.phoenixVideo.currentTime=0;this.refs.phoenixVideo.play().catch(()=>{})}

    resetExperience(){this.stopMix({silent:true});this.applyState({...DEFAULT_STATE},{restorePhrase:true});this.setMode('easy',{announce:false});this.goToScene(0,{focus:false});this.scrollToTheater();this.phrasePromptShown=false;this.announce('A new Studio journey is ready.')}

    /* ----------------------------------------------------------------------
       Motion, interaction, lifecycle
       ---------------------------------------------------------------------- */

    toggleCalm() {
      const calm=!this.root.classList.contains('is-calm-mode');this.root.classList.toggle('is-calm-mode',calm);this.updateCalmButtons();
      if(calm){this.pauseSceneVideo({preservePoster:false});if(this.masterGain&&this.audioContext)this.masterGain.gain.setTargetAtTime(.045,this.audioContext.currentTime,.08)}else if(this.masterGain&&this.audioContext&&this.soundEnabled)this.masterGain.gain.setTargetAtTime(this.outputLevel(this.state),this.audioContext.currentTime,.08);
      this.persistState();this.announce(calm?'Calm Mode is on.':'Cinematic Mode restored.');
    }
    updateCalmButtons(){const calm=this.root.classList.contains('is-calm-mode');qa(this.root,'[data-bk-scs4-calm]').forEach((button)=>{button.setAttribute('aria-pressed',String(calm));button.setAttribute('aria-label',calm?'Turn Calm Mode off':'Turn Calm Mode on')})}
    toggleMotion(){
      if(this.systemReducedMotion){this.root.classList.add('is-reduced-motion');this.updateMotionButton();this.announce('Your device Reduced Motion preference is active.');return;}
      const reduced=!this.root.classList.contains('is-reduced-motion');
      this.root.classList.toggle('is-reduced-motion',reduced);
      try{localStorage.setItem(`${this.config.storageKey}:motion`,reduced?'reduced':'full')}catch(_){ }
      this.updateMotionButton();
      if(reduced)this.pauseSceneVideo({preservePoster:false});
      else if(this.sceneVideoIntent==='auto'&&this.sceneVideos().length&&!this.saveData)this.scheduleSceneVideo(600);
      this.announce(reduced?'Reduced Motion is on.':'Motion restored.');
    }
    updateMotionButton(){const reduced=this.reduced();const button=q(this.root,'[data-bk-scs4-motion]');button?.setAttribute('aria-pressed',String(!reduced));button?.setAttribute('aria-label',reduced?'Motion is reduced':'Reduce motion');if(button){const label=q(button,'em');if(label)label.textContent=reduced?'Reduced':'Motion'}}

    setupStageTilt(){if(!this.refs.theater||!window.matchMedia?.('(pointer:fine)').matches)return;this.bind(this.refs.theater,'pointermove',(event)=>{if(this.reduced()||this.root.classList.contains('is-calm-mode'))return;const rect=this.refs.theater.getBoundingClientRect();const x=clamp((event.clientX-rect.left)/rect.width,0,1),y=clamp((event.clientY-rect.top)/rect.height,0,1);cancelAnimationFrame(this.tiltFrame);this.tiltFrame=requestAnimationFrame(()=>{this.root.style.setProperty('--bk-scs4-tilt-y',`${((x-.5)*2.6).toFixed(2)}deg`);this.root.style.setProperty('--bk-scs4-tilt-x',`${((.5-y)*2.1).toFixed(2)}deg`);this.root.style.setProperty('--bk-scs4-pointer-x',`${(x*100).toFixed(1)}%`);this.root.style.setProperty('--bk-scs4-pointer-y',`${(y*100).toFixed(1)}%`)})});this.bind(this.refs.theater,'pointerleave',()=>{this.root.style.setProperty('--bk-scs4-tilt-y','0deg');this.root.style.setProperty('--bk-scs4-tilt-x','0deg');this.root.style.setProperty('--bk-scs4-pointer-x','50%');this.root.style.setProperty('--bk-scs4-pointer-y','50%')});qa(this.root,'[data-bk-scs4-tilt-card]').forEach((card)=>{this.bind(card,'pointermove',(event)=>{if(this.reduced())return;const rect=card.getBoundingClientRect();const rx=((.5-(event.clientY-rect.top)/rect.height)*4).toFixed(2),ry=((((event.clientX-rect.left)/rect.width)-.5)*5).toFixed(2);card.style.transform=`perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-2px)`});this.bind(card,'pointerleave',()=>{card.style.transform=''})})}

    setupSwipe(){if(!this.refs.theater)return;this.bind(this.refs.theater,'pointerdown',(event)=>{if(event.pointerType==='mouse')return;this.swipeStart={x:event.clientX,y:event.clientY}});this.bind(this.refs.theater,'pointerup',(event)=>{if(!this.swipeStart)return;const dx=event.clientX-this.swipeStart.x,dy=event.clientY-this.swipeStart.y;this.swipeStart=null;if(Math.abs(dx)>55&&Math.abs(dx)>Math.abs(dy)*1.2)this.goToScene(this.currentScene+(dx<0?1:-1))})}

    setupVisibilityObserver(){if(!this.refs.theater||!('IntersectionObserver'in window))return;this.visibilityObserver=new IntersectionObserver((entries)=>{const entry=entries[0];this.isVisible=Boolean(entry?.isIntersecting&&entry.intersectionRatio>.18);if(!this.isVisible&&this.config.pauseOffscreen){this.pauseSceneVideo({preservePoster:true});if(this.isPlaying)this.stopMix({silent:true})}}, {threshold:[0,.18,.5]});this.visibilityObserver.observe(this.refs.theater)}
    handleVisibilityChange(){if(document.hidden){this.pauseSceneVideo({preservePoster:true});if(this.isPlaying)this.stopMix({silent:true});this.pauseBreathing()}else if(this.experienceStarted&&this.sceneVideoIntent==='auto'&&this.sceneVideos().length&&!this.reduced()&&!this.saveData)this.scheduleSceneVideo(600)}

    handleKeydown(event){
      const target=event.target;
      if(target?.closest?.('input,textarea,select,button,a,summary,[contenteditable="true"],[role="dialog"],[role="tablist"]'))return;
      if(event.key==='ArrowRight'){event.preventDefault();this.goToScene(this.currentScene+1)}
      else if(event.key==='ArrowLeft'){event.preventDefault();this.goToScene(this.currentScene-1)}
      else if(/^[1-6]$/.test(event.key)){const button=q(this.root,`[data-bk-scs4-beat-pad="${Number(event.key)-1}"]`);button?.click()}
      else if(event.code==='Space'){event.preventDefault();this.togglePlay()}
    }

    async toggleRecording(){if(!this.config.enableMic||!navigator.mediaDevices?.getUserMedia||!window.MediaRecorder){this.announce('Microphone recording is not available.');return}const button=q(this.root,'[data-bk-scs4-record]'),label=q(this.root,'[data-bk-scs4-record-label]');if(this.mediaRecorder?.state==='recording'){this.mediaRecorder.stop();return}try{this.mediaStream=await navigator.mediaDevices.getUserMedia({audio:true});this.recordedChunks=[];this.mediaRecorder=new MediaRecorder(this.mediaStream);this.mediaRecorder.addEventListener('dataavailable',(event)=>{if(event.data.size)this.recordedChunks.push(event.data)});this.mediaRecorder.addEventListener('stop',()=>{const blob=new Blob(this.recordedChunks,{type:this.mediaRecorder.mimeType||'audio/webm'});if(this.recordedVoiceUrl)URL.revokeObjectURL(this.recordedVoiceUrl);this.recordedVoiceUrl=URL.createObjectURL(blob);this.mediaStream?.getTracks().forEach((track)=>track.stop());this.mediaStream=null;if(label)label.textContent='Record again';button?.classList.remove('is-recording');clearTimeout(this.recordingTimer);this.announce('Session recording is ready in this tab. It is not uploaded by this code.')});this.mediaRecorder.start();button?.classList.add('is-recording');if(label)label.textContent='Stop recording';this.recordingTimer=setTimeout(()=>{if(this.mediaRecorder?.state==='recording')this.mediaRecorder.stop()},15000);this.announce('Recording started. Maximum 15 seconds.')}catch(_){this.announce('Microphone permission was not granted. You can keep the words unspoken.') }}

    announce(message){if(!this.refs.status)return;this.refs.status.textContent=message;this.refs.status.classList.add('is-visible');clearTimeout(this.toastTimer);this.toastTimer=setTimeout(()=>this.refs.status.classList.remove('is-visible'),4200)}
    titleCase(value){return String(value||'').replace(/[-_]/g,' ').replace(/\b\w/g,(letter)=>letter.toUpperCase())}
    slugify(value){return String(value||'bacci-kid-sound-card').toLowerCase().trim().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,72)||'bacci-kid-sound-card'}
    mixHex(a,b,weight=.5){const parse=(value)=>{const clean=String(value||'#000000').replace('#','');const full=clean.length===3?clean.split('').map((character)=>character+character).join(''):clean.padEnd(6,'0').slice(0,6);return[parseInt(full.slice(0,2),16),parseInt(full.slice(2,4),16),parseInt(full.slice(4,6),16)]};const first=parse(a),second=parse(b),result=first.map((number,index)=>Math.round(number*(1-weight)+second[index]*weight));return`#${result.map((number)=>number.toString(16).padStart(2,'0')).join('')}`}
    hexToRgba(hex,alpha){const clean=String(hex||'#000').replace('#','');const full=clean.length===3?clean.split('').map((c)=>c+c).join(''):clean;const number=parseInt(full,16);return`rgba(${(number>>16)&255},${(number>>8)&255},${number&255},${alpha})`}
    drawGlow(ctx,x,y,radius,color,alpha){const gradient=ctx.createRadialGradient(x,y,0,x,y,radius);gradient.addColorStop(0,this.hexToRgba(color,alpha));gradient.addColorStop(1,this.hexToRgba(color,0));ctx.fillStyle=gradient;ctx.fillRect(x-radius,y-radius,radius*2,radius*2)}
    roundRect(ctx,x,y,width,height,radius){const r=Math.min(radius,width/2,height/2);ctx.beginPath();ctx.moveTo(x+r,y);ctx.arcTo(x+width,y,x+width,y+height,r);ctx.arcTo(x+width,y+height,x,y+height,r);ctx.arcTo(x,y+height,x,y,r);ctx.arcTo(x,y,x+width,y,r);ctx.closePath()}
    wrapText(ctx,text,x,y,maxWidth,lineHeight){const words=String(text||'').split(/\s+/);let line='',lineY=y;words.forEach((word,index)=>{const test=`${line}${word} `;if(ctx.measureText(test).width>maxWidth&&line){ctx.fillText(line.trim(),x,lineY);line=word+' ';lineY+=lineHeight}else line=test;if(index===words.length-1)ctx.fillText(line.trim(),x,lineY)});return lineY}

    destroy(){clearTimeout(this.sceneVideoTimer);clearTimeout(this.sceneGapTimer);clearTimeout(this.schedulerTimer);clearTimeout(this.toastTimer);clearTimeout(this.recordingTimer);clearTimeout(this.patternPreviewTimer);clearTimeout(this.sonicTimer);this.clearHeartbeatTimers();this.stopThemeSong();clearInterval(this.breathTimer);cancelAnimationFrame(this.animationFrame);cancelAnimationFrame(this.particleFrame);cancelAnimationFrame(this.tiltFrame);this.stopMix({silent:true});this.pauseSceneVideo({preservePoster:true});this.cleanupBreathing();this.visibilityObserver?.disconnect();this.bound.forEach(([node,event,handler,options])=>node.removeEventListener(event,handler,options));this.bound=[];if(this.recordedVoiceUrl)URL.revokeObjectURL(this.recordedVoiceUrl);this.mediaStream?.getTracks().forEach((track)=>track.stop());try{this.audioContext?.close()}catch(_){}window.setTimeout(()=>{if(!document.querySelector('[data-bk-scs4]'))document.body.classList.remove('bk-scs4-page','bk-scs4-welcome-open')},0)}
  }

  const mount=(root)=>{if(!root||instances.has(root))return;instances.set(root,new BacciSoundColorStudio(root))};
  const unmount=(root)=>{const instance=instances.get(root);instance?.destroy();instances.delete(root)};
  const mountAll=(scope=document)=>qa(scope,'[data-bk-scs4]').forEach(mount);

  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>mountAll(),{once:true});else mountAll();
  document.addEventListener('shopify:section:load',(event)=>mountAll(event.target));
  document.addEventListener('shopify:section:unload',(event)=>{const root=event.target.matches?.('[data-bk-scs4]')?event.target:q(event.target,'[data-bk-scs4]');if(root)unmount(root)});
})();






/* ============================================================================
   BACCI KID SOUND + COLOR STUDIO — RESPONSIVE HEARTBEAT INTRO v9.1
   COMPLETE replacement for the previous v8.x intro block.
   Uses landscape, square, and portrait artwork by device class.
   ============================================================================ */

(() => {
  'use strict';

  const VERSION = '9.1.0-responsive-heartbeat';
  const FALLBACK = 'https://baccikid.b-cdn.net/Enter%20The%20Sound%20Color%20Studio/bk-teen-sc-01-enter.webp';

  const DEFAULT_LANDSCAPE = [
    {
      src: FALLBACK,
      alt: 'Enter the Bacci Kid Sound and Color Studio.'
    },
    {
      src: 'https://baccikid.b-cdn.net/Enter%20The%20Sound%20Color%20Studio/Feel%20The%20Beat%20Neon%20Lights%20(heartbeat)%20Sound%20%2B%20Color%20Studio%20V1.0001.0002.png',
      alt: 'Feel the Beat in Neon Lights. Color becomes rhythm and rhythm becomes courage.'
    },
    {
      src: 'https://baccikid.b-cdn.net/Enter%20The%20Sound%20Color%20Studio/You%20Are%20Part%20of%20Bacci%27s%20Heartbeat%20(heartbeat)%20Sound%20%2B%20Color%20Studio%20V1.0001.0003.png',
      alt: 'You are part of Bacci Kid’s heartbeat. Your light helps the Studio come alive.'
    },
    {
      src: 'https://baccikid.b-cdn.net/Enter%20The%20Sound%20Color%20Studio/Thank%20You%20For%20Being%20Part%20of%20Bacci%20Kid%27s%20Heartbeat%20(heartbeat)%20Sound%20%2B%20Color%20Studio%20V1.0001.0005.png',
      alt: 'Thank you for being part of Bacci Kid’s heartbeat.'
    }
  ];

  const VERIFIED_TABLET_PORTRAIT = 'https://baccikid.b-cdn.net/Enter%20The%20Sound%20Color%20Studio/Part%20of%20Bacci%20Kid%27s%20Heartbeat%20(heartbeat%20)%20Sound%20%2B%20Color%20Studio%20V1.0001.0002.png';
  const VERIFIED_PHONE_PORTRAIT = 'https://baccikid.b-cdn.net/Enter%20The%20Sound%20Color%20Studio/Thank%20You%20For%20Being%20Part%20of%20Bacci%20Kid%27s%20Heartbeat%20(heartbeat)%20Sound%20%2B%20Color%20Studio%20Portrait%20View%20-%20V1.0001.0005.png';

  const mounted = new WeakMap();

  function readStudioConfig(root) {
    try {
      return JSON.parse(root.querySelector('[data-bk-scs4-config]')?.textContent || '{}');
    } catch (error) {
      console.error('Bacci Kid responsive heartbeat configuration error', error);
      return {};
    }
  }

  function uniqueSlides(slides) {
    const seen = new Set();
    return slides.filter((slide) => {
      const src = String(slide?.src || '').trim();
      if (!src || seen.has(src)) return false;
      seen.add(src);
      return true;
    });
  }

  function sequencesFor(root) {
    const config = readStudioConfig(root);
    const art = config.heartbeatArt || {};
    const desktop = String(art.desktop || config.heartbeatImageUrl || FALLBACK).trim();
    const tablet = String(art.tabletPortrait || VERIFIED_TABLET_PORTRAIT).trim();
    const phone = String(art.mobilePortrait || VERIFIED_PHONE_PORTRAIT).trim();
    return {
      landscape: uniqueSlides([
        { src: desktop, alt: 'Welcome to the Bacci Kid Sound and Color Studio.' },
        ...DEFAULT_LANDSCAPE
      ]),
      tabletPortrait: [{
        src: tablet || VERIFIED_TABLET_PORTRAIT,
        alt: 'You are part of Bacci Kid’s heartbeat. Your light adds to the beat.'
      }],
      phone: [{
        src: phone || VERIFIED_PHONE_PORTRAIT,
        alt: 'Thank you for being part of Bacci Kid’s heartbeat. Feel the beat and create your sound.'
      }]
    };
  }

  function reduced(root) {
    return root.classList.contains('is-reduced-motion') ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  function viewportClass() {
    const width = Math.min(
      window.innerWidth || Infinity,
      document.documentElement.clientWidth || Infinity,
      window.visualViewport?.width || Infinity
    );
    const height = Math.min(
      window.innerHeight || Infinity,
      document.documentElement.clientHeight || Infinity,
      window.visualViewport?.height || Infinity
    );
    const portrait = height > width;
    if (width <= 760) return 'phone';
    if (portrait && width <= 1180) return 'tabletPortrait';
    return 'landscape';
  }

  function sequenceFor(sequences, type) {
    if (type === 'phone') return sequences.phone;
    if (type === 'tabletPortrait') return sequences.tabletPortrait;
    return sequences.landscape;
  }

  function durationFor(type) {
    if (type === 'phone') return 10500;
    if (type === 'tabletPortrait') return 9500;
    return 8500;
  }

  function preload(slides) {
    slides.forEach(({ src }) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
    });
  }

  function button(className, label, icon) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = className;
    el.setAttribute('aria-label', label);
    el.innerHTML = `<span aria-hidden="true">${icon}</span><em>${label}</em>`;
    return el;
  }

  function mount(root) {
    if (!root || mounted.has(root)) return;

    const welcome = root.querySelector('[data-bk-scs4-welcome]');
    const panel = root.querySelector('[data-bk-scs7-welcome-stage="heartbeat"]');
    if (!welcome || !panel) return;

    const addBeat = panel.querySelector('[data-bk-scs7-add-beat]');
    const skip = panel.querySelector('[data-bk-scs7-skip-heartbeat]');
    if (!addBeat || !skip) return;

    welcome.dataset.bkScs9Intro = 'true';
    panel.dataset.bkScs9Intro = 'true';
    root.dataset.heartbeatVersion = VERSION;

    panel.innerHTML = `
      <div class="bk-scs9__intro" data-bk-scs9-intro>
        <header class="bk-scs9__utility">
          <div class="bk-scs9__brand">
            <strong>Bacci Kid</strong>
            <small>Sound + Color Studio</small>
          </div>
          <div class="bk-scs9__utility-actions" data-bk-scs9-utility></div>
        </header>
        <div class="bk-scs9__media" data-bk-scs9-media>
          <img class="bk-scs9__ambient" data-bk-scs9-ambient alt="" aria-hidden="true" decoding="async">
          <img class="bk-scs9__art" data-bk-scs9-art alt="" decoding="async" fetchpriority="high">
          <p class="bk-scs9__sr-status" data-bk-scs9-status aria-live="polite"></p>
        </div>
        <footer class="bk-scs9__dock">
          <div class="bk-scs9__progress-wrap">
            <div class="bk-scs9__progress" data-bk-scs9-progress></div>
            <span class="bk-scs9__scene-label" data-bk-scs9-label></span>
          </div>
          <div class="bk-scs9__actions" data-bk-scs9-actions></div>
        </footer>
      </div>`;

    const intro = panel.querySelector('[data-bk-scs9-intro]');
    const art = panel.querySelector('[data-bk-scs9-art]');
    const ambient = panel.querySelector('[data-bk-scs9-ambient]');
    const status = panel.querySelector('[data-bk-scs9-status]');
    const progress = panel.querySelector('[data-bk-scs9-progress]');
    const label = panel.querySelector('[data-bk-scs9-label]');
    const utility = panel.querySelector('[data-bk-scs9-utility]');
    const actions = panel.querySelector('[data-bk-scs9-actions]');

    skip.classList.add('bk-scs9__skip');
    utility.appendChild(skip);
    addBeat.classList.add('bk-scs9__enter');
    actions.appendChild(addBeat);

    const pause = button('bk-scs9__control bk-scs9__pause', 'Pause intro', 'Ⅱ');
    const sound = button('bk-scs9__control bk-scs9__sound', 'Hear heartbeat', '♡');
    sound.setAttribute('aria-pressed', 'false');
    utility.insertBefore(pause, skip);
    actions.insertBefore(sound, addBeat);

    const sequences = sequencesFor(root);
    let type = viewportClass();
    let slides = sequenceFor(sequences, type);
    let index = 0;
    let timer = 0;
    let paused = false;
    let alive = true;
    let audioContext = null;
    let audioTimer = 0;
    let soundOn = false;
    let resizeTimer = 0;
    let dots = [];

    function clearTimer() {
      window.clearTimeout(timer);
      timer = 0;
    }

    function rebuildDots() {
      progress.innerHTML = '';
      dots = slides.map((_, dotIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bk-scs9__dot';
        dot.setAttribute('aria-label', `Show intro scene ${dotIndex + 1}`);
        dot.addEventListener('click', () => {
          show(dotIndex, true);
          schedule();
        });
        progress.appendChild(dot);
        return dot;
      });
    }

    function updatePulse() {
      intro.classList.toggle(
        'is-pulsing',
        alive && !paused && !reduced(root) && !panel.hidden
      );
    }

    function applySlide(slide) {
      const loader = new Image();
      loader.decoding = 'async';
      loader.onload = () => {
        if (!alive) return;
        art.src = slide.src;
        ambient.src = slide.src;
        art.alt = slide.alt;
        status.textContent = slide.alt;
        requestAnimationFrame(() => art.classList.add('is-ready'));
      };
      loader.onerror = () => {
        if (!alive) return;
        art.src = FALLBACK;
        ambient.src = FALLBACK;
        art.alt = 'Enter the Bacci Kid Sound and Color Studio.';
        status.textContent = art.alt;
        requestAnimationFrame(() => art.classList.add('is-ready'));
      };
      loader.src = slide.src;
    }

    function show(next, manual = false) {
      if (!alive || !slides.length) return;
      index = Math.max(0, Math.min(next, slides.length - 1));
      const slide = slides[index];
      art.classList.remove('is-ready');
      window.setTimeout(() => applySlide(slide), manual ? 60 : 190);
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      label.textContent = `Scene ${index + 1} of ${slides.length}`;
      intro.classList.toggle('is-final', index === slides.length - 1);
    }

    function schedule() {
      clearTimer();
      if (!alive || paused || reduced(root) || panel.hidden) return;
      if (index >= slides.length - 1) return;
      timer = window.setTimeout(() => {
        show(index + 1);
        schedule();
      }, durationFor(type));
    }

    function stopSound() {
      window.clearInterval(audioTimer);
      audioTimer = 0;
      soundOn = false;
      sound.setAttribute('aria-pressed', 'false');
      sound.setAttribute('aria-label', 'Hear heartbeat');
      sound.querySelector('em').textContent = 'Hear heartbeat';
      sound.querySelector('span').textContent = '♡';
    }

    function heartbeatCycle() {
      if (!audioContext || audioContext.state !== 'running') return;
      const now = audioContext.currentTime + .02;
      [0, .19].forEach((offset, second) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const filter = audioContext.createBiquadFilter();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(second ? 61 : 53, now + offset);
        filter.type = 'lowpass';
        filter.frequency.value = 155;
        gain.gain.setValueAtTime(.0001, now + offset);
        gain.gain.exponentialRampToValueAtTime(second ? .028 : .042, now + offset + .025);
        gain.gain.exponentialRampToValueAtTime(.0001, now + offset + .15);
        osc.connect(filter).connect(gain).connect(audioContext.destination);
        osc.start(now + offset);
        osc.stop(now + offset + .18);
      });
    }

    async function toggleSound() {
      if (soundOn) {
        stopSound();
        return;
      }
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) return;
      audioContext ||= new Context();
      if (audioContext.state === 'suspended') await audioContext.resume();
      soundOn = true;
      sound.setAttribute('aria-pressed', 'true');
      sound.setAttribute('aria-label', 'Mute heartbeat');
      sound.querySelector('em').textContent = 'Heartbeat on';
      sound.querySelector('span').textContent = '♥';
      heartbeatCycle();
      audioTimer = window.setInterval(heartbeatCycle, 1720);
    }

    function togglePause() {
      paused = !paused;
      pause.setAttribute('aria-label', paused ? 'Resume intro' : 'Pause intro');
      pause.querySelector('em').textContent = paused ? 'Resume intro' : 'Pause intro';
      pause.querySelector('span').textContent = paused ? '▶' : 'Ⅱ';
      updatePulse();
      if (paused) clearTimer(); else schedule();
    }

    function handleVisibility() {
      if (document.hidden) clearTimer();
      else if (!paused) schedule();
      updatePulse();
    }

    function reclassify() {
      const nextType = viewportClass();
      if (nextType === type) return;
      type = nextType;
      slides = sequenceFor(sequences, type);
      index = 0;
      preload(slides);
      rebuildDots();
      show(0, true);
      schedule();
    }

    function handleResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(reclassify, 180);
    }

    pause.addEventListener('click', togglePause);
    sound.addEventListener('click', toggleSound);
    window.addEventListener('resize', handleResize, { passive: true });
    window.visualViewport?.addEventListener('resize', handleResize, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);

    const observer = new MutationObserver(() => {
      if (panel.hidden) {
        clearTimer();
        stopSound();
      } else if (!paused) {
        schedule();
      }
      updatePulse();
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['hidden', 'class'] });

    preload(slides);
    rebuildDots();
    show(0, true);
    updatePulse();
    schedule();

    mounted.set(root, {
      destroy() {
        alive = false;
        clearTimer();
        stopSound();
        window.clearTimeout(resizeTimer);
        observer.disconnect();
        window.removeEventListener('resize', handleResize);
        window.visualViewport?.removeEventListener('resize', handleResize);
        document.removeEventListener('visibilitychange', handleVisibility);
        if (audioContext && audioContext.state !== 'closed') audioContext.close().catch(() => {});
      }
    });
  }

  function unmount(root) {
    const instance = mounted.get(root);
    instance?.destroy();
    mounted.delete(root);
  }

  function mountAll(scope = document) {
    if (scope.matches?.('[data-bk-scs4]')) mount(scope);
    scope.querySelectorAll?.('[data-bk-scs4]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-bk-scs4]')
      ? event.target
      : event.target.querySelector?.('[data-bk-scs4]');
    if (root) unmount(root);
  });
})();

/* ============================================================================
   BACCI KID SOUND + COLOR STUDIO • RECOVERY CONTROLLER v10.2.0
   Final authority for page chrome, Enter Studio, comfort choices, route choice,
   welcome closure, and stale scroll-lock cleanup.  It intentionally preserves
   the existing scene, audio, guide, realm, download, and commercial engines.
   ============================================================================ */
(() => {
  'use strict';

  const mounted = new WeakMap();
  const PAGE_CLASS = 'bk-scs-force-page';
  const OPEN_CLASSES = ['bk-scs4-welcome-open', 'bk-scs10-onboarding-open'];

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => Array.from(root?.querySelectorAll(selector) || []);

  function parseConfig(root) {
    try {
      return JSON.parse(q(root, '[data-bk-scs4-config]')?.textContent || '{}');
    } catch (error) {
      console.error('Bacci Kid Studio recovery configuration error', error);
      return {};
    }
  }

  function applyPageShell() {
    document.documentElement.classList.add(PAGE_CLASS);
    document.body?.classList.add(PAGE_CLASS, 'bk-scs4-page');
  }

  function lockPage() {
    applyPageShell();
    OPEN_CLASSES.forEach((name) => {
      document.documentElement.classList.add(name);
      document.body?.classList.add(name);
    });
  }

  function unlockPage() {
    OPEN_CLASSES.forEach((name) => {
      document.documentElement.classList.remove(name);
      document.body?.classList.remove(name);
    });

    /* Older patches sometimes wrote overflow directly. Remove only the stale
       lock value; never overwrite another deliberate inline value. */
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.removeProperty('overflow');
    }
    if (document.body?.style.overflow === 'hidden') {
      document.body.style.removeProperty('overflow');
    }
  }

  function mount(root) {
    if (!root || mounted.has(root)) return;

    applyPageShell();

    const config = parseConfig(root);
    const welcome = q(root, '[data-bk-scs4-welcome]');
    const stages = qa(root, '[data-bk-scs7-welcome-stage]');
    if (!welcome || stages.length === 0) return;

    const sectionId = root.dataset.sectionId || 'studio';
    const storageKey = config.storageKey || 'bk-sound-color-studio-v7';
    const version = root.dataset.version || '10.2.0-recovery';
    const welcomeKey = `${storageKey}:welcome:${version}:${sectionId}`;
    let destroyed = false;
    let syncing = false;

    function stageNode(name) {
      return stages.find((stage) => stage.dataset.bkScs7WelcomeStage === name) || null;
    }

    function firstFocusable(stage) {
      return q(stage, '[data-bk-scs7-add-beat], [data-bk-scs7-skip-heartbeat], [data-bk-scs7-entrance-mode], [data-bk-scs4-start-easy], button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
    }

    function markSeen() {
      try { sessionStorage.setItem(welcomeKey, '1'); } catch (_) {}
    }

    function showStage(name, options = {}) {
      if (destroyed || syncing) return;
      const active = stageNode(name);
      if (!active) return;
      syncing = true;

      welcome.hidden = false;
      welcome.removeAttribute('aria-hidden');
      welcome.dataset.bkScs10Stage = name;
      root.dataset.bkScs10WelcomeStage = name;

      stages.forEach((stage) => {
        const selected = stage === active;
        stage.hidden = !selected;
        stage.classList.toggle('is-active', selected);
        stage.setAttribute('aria-hidden', String(!selected));
        if (selected) stage.removeAttribute('inert');
        else stage.setAttribute('inert', '');
      });

      const heartbeat = stageNode('heartbeat');
      if (name === 'heartbeat') {
        welcome.dataset.bkScs9Intro = 'true';
        heartbeat?.setAttribute('data-bk-scs9-intro', 'true');
      } else {
        welcome.removeAttribute('data-bk-scs9-intro');
        heartbeat?.removeAttribute('data-bk-scs9-intro');
      }

      lockPage();
      syncing = false;

      requestAnimationFrame(() => {
        try { welcome.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (_) { welcome.scrollTop = 0; }
        active.scrollTop = 0;
        if (options.focus !== false) firstFocusable(active)?.focus({ preventScroll: true });
      });
    }

    function setModeUI(mode) {
      const next = mode === 'pro' ? 'pro' : 'easy';
      root.dataset.mode = next;
      q(root, '[data-bk-scs4-easy]')?.toggleAttribute('hidden', next !== 'easy');
      q(root, '[data-bk-scs4-pro]')?.toggleAttribute('hidden', next !== 'pro');
      qa(root, '[data-bk-scs4-mode]').forEach((button) => {
        const active = button.dataset.bkScs4Mode === next;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    function closeWelcome(mode, options = {}) {
      if (destroyed) return;
      markSeen();
      setModeUI(mode);
      root.classList.add('is-started');
      welcome.hidden = true;
      welcome.setAttribute('aria-hidden', 'true');
      welcome.dataset.bkScs10Stage = 'closed';
      root.dataset.bkScs10WelcomeStage = 'closed';
      stages.forEach((stage) => {
        stage.setAttribute('aria-hidden', 'true');
        stage.setAttribute('inert', '');
      });
      unlockPage();

      const target = options.intro
        ? q(root, '[data-bk-scs4-theater]')
        : q(root, mode === 'pro' ? '[data-bk-scs4-pro]' : '[data-bk-scs4-easy]') || q(root, '.bk-scs4__console');

      window.setTimeout(() => {
        target?.scrollIntoView({
          behavior: root.classList.contains('is-reduced-motion') ? 'auto' : 'smooth',
          block: 'start'
        });
      }, 90);
    }

    function applyEntranceMode(mode) {
      root.classList.toggle('is-calm-mode', mode === 'calm');
      if (mode === 'reduced') {
        root.classList.add('is-reduced-motion');
      } else if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        root.classList.remove('is-reduced-motion');
      }
      qa(root, '[data-bk-scs4-calm]').forEach((button) => {
        button.setAttribute('aria-pressed', String(root.classList.contains('is-calm-mode')));
      });
      qa(root, '[data-bk-scs4-motion]').forEach((button) => {
        button.setAttribute('aria-pressed', String(!root.classList.contains('is-reduced-motion')));
      });
    }

    function reinforce(action, delay = 0) {
      queueMicrotask(action);
      requestAnimationFrame(action);
      window.setTimeout(action, delay);
    }

    function handleClick(event) {
      const control = event.target.closest('button, a');
      if (!control || !root.contains(control)) return;

      if (control.matches('[data-bk-scs7-add-beat], [data-bk-scs7-skip-heartbeat], .bk-scs10__utility-enter')) {
        const next = config.showAccessibilityEntrance === false ? 'route' : 'access';
        reinforce(() => showStage(next), 920);
        return;
      }

      const entrance = control.closest('[data-bk-scs7-entrance-mode]');
      if (entrance) {
        const mode = entrance.dataset.bkScs7EntranceMode || 'standard';
        reinforce(() => {
          applyEntranceMode(mode);
          showStage('route');
        }, 120);
        return;
      }

      if (control.closest('[data-bk-scs4-start-easy]')) {
        reinforce(() => closeWelcome('easy'), 160);
        return;
      }

      if (control.closest('[data-bk-scs4-start-pro]')) {
        reinforce(() => closeWelcome('pro'), 160);
        return;
      }

      if (control.closest('[data-bk-scs4-watch-intro]')) {
        reinforce(() => showStage('heartbeat'), 120);
      }
    }

    /* Capture phase guarantees this fallback sees the click even if an older
       enhancement later removes/reparents the original button. */
    root.addEventListener('click', handleClick, true);

    const observer = new MutationObserver(() => {
      if (destroyed || syncing) return;
      if (welcome.hidden || welcome.getAttribute('aria-hidden') === 'true') {
        unlockPage();
        root.classList.add('is-started');
        return;
      }
      lockPage();
    });
    observer.observe(welcome, { attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class'] });

    /* Make the live state deterministic after all earlier enhancement IIFEs
       have finished their first DOM pass. */
    requestAnimationFrame(() => {
      if (welcome.hidden) unlockPage();
      else lockPage();
    });

    mounted.set(root, {
      destroy() {
        destroyed = true;
        observer.disconnect();
        root.removeEventListener('click', handleClick, true);
        unlockPage();
      }
    });
  }

  function unmount(root) {
    mounted.get(root)?.destroy();
    mounted.delete(root);
  }

  function mountAll(scope = document) {
    if (scope.matches?.('[data-bk-scs4]')) mount(scope);
    qa(scope, '[data-bk-scs4]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-bk-scs4]')
      ? event.target
      : q(event.target, '[data-bk-scs4]');
    if (root) unmount(root);
    requestAnimationFrame(() => {
      if (document.querySelector('[data-bk-scs4]')) return;
      document.documentElement.classList.remove(PAGE_CLASS, ...OPEN_CLASSES);
      document.body?.classList.remove(PAGE_CLASS, 'bk-scs4-page', ...OPEN_CLASSES);
    });
  });
})();



/* ============================================================================
   BACCI KID SOUND + COLOR STUDIO • CREATION PULSE v10.3.1
   Adaptive live preview drawer + 30-state creative history.
   It does not own onboarding and never starts sound automatically.
   ============================================================================ */
(() => {
  'use strict';

  if (window.BacciKidCreationPulseV1031) return;
  window.BacciKidCreationPulseV1031 = true;

  const VERSION = '10.3.1-creation-pulse';
  const mounted = new WeakMap();
  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => Array.from(root?.querySelectorAll(selector) || []);
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const titleCase = (value) => String(value || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const PATTERNS = {
    heartbeat: [1, .72, 0, 0, .88, .58, 0, 0],
    pulse: [.92, 0, .72, 0, .92, 0, .72, 0],
    bounce: [1, 0, .55, .72, 0, .8, .48, 0],
    flow: [.68, 0, 0, .45, .76, 0, 0, .5],
    spark: [.92, .35, 0, .72, .4, .8, 0, .62],
    drift: [.6, 0, .34, 0, .52, 0, .26, .42],
    orbit: [.78, .22, .46, 0, .72, .18, .56, .34]
  };

  const EASY_WORLDS = {
    midnight: { rhythm:'heartbeat', instrument:'bass', ambient:'rain', tempo:68, energy:42, texture:'deep' },
    rocket: { rhythm:'bounce', instrument:'drums', ambient:'night', tempo:118, energy:86, texture:'glassy' },
    ocean: { rhythm:'flow', instrument:'harp', ambient:'ocean', tempo:72, energy:46, texture:'airy' },
    golden: { rhythm:'pulse', instrument:'violin', ambient:'forest', tempo:92, energy:72, texture:'cinematic' }
  };

  const RESET_STATE = {
    title:'My Sound + Color Moment',
    color:'#8a65ff', colorName:'Violet', customActive:false,
    energyWord:'Quiet', rhythm:'heartbeat', instrument:'keys', layerInstrument:'none',
    harmony:'fifth', layerLevel:36, ambient:'rain', tempo:82, energy:55,
    ambientLevel:45, reverb:28, echo:12, space:45, texture:'warm',
    masterVolume:86, phrase:'', voice:'silent', includePhrase:false,
    easyColor:'#8a65ff', easyVibe:'Quiet', easyWorld:'midnight'
  };

  function parseHex(hex) {
    const value = String(hex || '#8a65ff').trim().replace('#', '');
    const normalized = value.length === 3 ? value.split('').map((char) => char + char).join('') : value.padEnd(6, '0').slice(0, 6);
    const number = Number.parseInt(normalized, 16);
    return Number.isFinite(number)
      ? { r:(number >> 16) & 255, g:(number >> 8) & 255, b:number & 255 }
      : { r:138, g:101, b:255 };
  }

  function rgba(hex, alpha) {
    const { r, g, b } = parseHex(hex);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  function findRadio(root, prefix) {
    return q(root, `input[name^="${prefix}-"]:checked`);
  }

  function setRadio(root, prefix, value) {
    const expected = String(value ?? '');
    const target = qa(root, `input[name^="${prefix}-"]`).find((input) => input.value === expected);
    if (target) target.checked = true;
    return target || null;
  }

  function mount(root) {
    if (!root || mounted.has(root)) return;
    const drawer = q(root, '[data-bk-scs11-preview]');
    const toggle = q(root, '[data-bk-scs11-preview-toggle]');
    if (!drawer || !toggle) return;

    root.dataset.creationPulseVersion = VERSION;

    const refs = {
      drawer,
      toggle,
      close:q(root, '[data-bk-scs11-preview-close]'),
      scrim:q(root, '[data-bk-scs11-scrim]'),
      canvas:q(root, '[data-bk-scs11-canvas]'),
      orb:q(root, '[data-bk-scs11-orb]'),
      title:q(root, '[data-bk-scs11-title]'),
      phrase:q(root, '[data-bk-scs11-phrase]'),
      chips:q(root, '[data-bk-scs11-chips]'),
      hear:q(root, '[data-bk-scs11-hear]'),
      stop:q(root, '[data-bk-scs11-stop]'),
      volume:q(root, '[data-bk-scs11-volume]'),
      volumeOutput:q(root, '[data-bk-scs11-volume-output]'),
      status:q(root, '[data-bk-scs11-status]'),
      resetDialog:q(root, '[data-bk-scs11-reset-dialog]'),
      resetConfirm:q(root, '[data-bk-scs11-reset-confirm]'),
      snap:q(root, '[data-bk-scs11-sheet-snap]')
    };

    const history = [];
    let historyIndex = -1;
    let restoring = false;
    let historyTimer = 0;
    let frame = 0;
    let animationFrame = 0;
    let resizeFrame = 0;
    let auditionArmed = false;
    let lastFocus = null;
    const bound = [];

    const bind = (node, event, handler, options) => {
      if (!node) return;
      node.addEventListener(event, handler, options);
      bound.push([node, event, handler, options]);
    };

    function status(message) {
      if (refs.status) refs.status.textContent = message;
    }

    function captureState() {
      const custom = q(root, '[data-bk-scs4-custom-color]');
      const fullColor = findRadio(root, 'bk-scs4-color');
      const easyColor = findRadio(root, 'bk-scs4-easy-color');
      const easyVibe = findRadio(root, 'bk-scs4-easy-vibe');
      const easyWorld = findRadio(root, 'bk-scs4-easy-world');
      const easyMode = root.dataset.mode !== 'pro';
      const customActive = custom?.dataset.active === 'true';
      const colorSource = easyMode && easyColor ? easyColor : fullColor;
      const color = customActive && !easyMode ? custom.value : (colorSource?.value || '#8a65ff');
      const colorName = customActive && !easyMode ? 'Custom' : (colorSource?.dataset.name || titleCase(colorSource?.title || 'Violet'));
      const world = easyWorld?.value || 'midnight';
      const worldPreset = EASY_WORLDS[world] || {};
      const read = (selector, fallback) => q(root, selector)?.value ?? fallback;
      const number = (selector, fallback) => Number(read(selector, fallback));
      const fullEnergyWord = findRadio(root, 'bk-scs4-energy-word')?.value || 'Quiet';
      return {
        title:String(read('[data-bk-scs4-title-input]', 'My Sound + Color Moment')).trim() || 'My Sound + Color Moment',
        color, colorName, customActive,
        energyWord:easyMode && easyVibe ? easyVibe.value : fullEnergyWord,
        rhythm:easyMode ? (worldPreset.rhythm || findRadio(root, 'bk-scs4-rhythm')?.value || 'heartbeat') : (findRadio(root, 'bk-scs4-rhythm')?.value || 'heartbeat'),
        instrument:easyMode ? (worldPreset.instrument || findRadio(root, 'bk-scs4-instrument')?.value || 'keys') : (findRadio(root, 'bk-scs4-instrument')?.value || 'keys'),
        layerInstrument:String(read('[data-bk-scs4-layer-instrument]', 'none')),
        harmony:String(read('[data-bk-scs4-harmony]', 'fifth')),
        layerLevel:number('[data-bk-scs4-layer-level]', 36),
        ambient:easyMode ? (worldPreset.ambient || findRadio(root, 'bk-scs4-ambient')?.value || 'rain') : (findRadio(root, 'bk-scs4-ambient')?.value || 'rain'),
        tempo:easyMode ? (worldPreset.tempo || number('[data-bk-scs4-tempo]', 82)) : number('[data-bk-scs4-tempo]', 82),
        energy:easyMode ? (worldPreset.energy || number('[data-bk-scs4-energy]', 55)) : number('[data-bk-scs4-energy]', 55),
        ambientLevel:number('[data-bk-scs4-ambient-level]', 45),
        reverb:number('[data-bk-scs4-reverb]', 28),
        echo:number('[data-bk-scs4-echo]', 12),
        space:number('[data-bk-scs4-space]', 45),
        texture:easyMode ? (worldPreset.texture || findRadio(root, 'bk-scs4-texture')?.value || 'warm') : (findRadio(root, 'bk-scs4-texture')?.value || 'warm'),
        masterVolume:number('[data-bk-scs4-master-volume]', 86),
        phrase:String(read('[data-bk-scs4-phrase]', '')).trim(),
        voice:String(read('[data-bk-scs4-voice]', 'silent')),
        includePhrase:Boolean(q(root, '[data-bk-scs4-include-phrase]')?.checked),
        easyColor:easyColor?.value || color,
        easyVibe:easyVibe?.value || 'Quiet',
        easyWorld:world
      };
    }

    function signature(state) {
      return JSON.stringify(state);
    }

    function updateHistoryButtons() {
      const canUndo = historyIndex > 0;
      const canRedo = historyIndex >= 0 && historyIndex < history.length - 1;
      qa(root, '[data-bk-scs11-undo]').forEach((button) => { button.disabled = !canUndo; });
      qa(root, '[data-bk-scs11-redo]').forEach((button) => { button.disabled = !canRedo; });
    }

    function pushHistory(state = captureState(), message = '') {
      if (restoring) return;
      const snapshot = typeof structuredClone === 'function' ? structuredClone(state) : JSON.parse(JSON.stringify(state));
      const nextSignature = signature(snapshot);
      if (historyIndex >= 0 && signature(history[historyIndex]) === nextSignature) return;
      if (historyIndex < history.length - 1) history.splice(historyIndex + 1);
      history.push(snapshot);
      if (history.length > 30) history.shift();
      historyIndex = history.length - 1;
      updateHistoryButtons();
      if (message) status(message);
    }

    function setValue(selector, value) {
      const node = q(root, selector);
      if (node && value !== undefined && value !== null) node.value = String(value);
      return node;
    }

    function applySnapshot(snapshot, message) {
      if (!snapshot) return;
      restoring = true;
      const stop = qa(root, '[data-bk-scs4-stop]').find((button) => !button.disabled);
      stop?.click();

      const custom = q(root, '[data-bk-scs4-custom-color]');
      if (snapshot.customActive && custom) {
        custom.value = snapshot.color;
        custom.dataset.active = 'true';
        qa(root, 'input[name^="bk-scs4-color-"]').forEach((input) => { input.checked = false; });
      } else {
        setRadio(root, 'bk-scs4-color', snapshot.color);
        if (custom) custom.dataset.active = 'false';
      }
      setRadio(root, 'bk-scs4-energy-word', snapshot.energyWord);
      setRadio(root, 'bk-scs4-rhythm', snapshot.rhythm);
      setRadio(root, 'bk-scs4-instrument', snapshot.instrument);
      setRadio(root, 'bk-scs4-ambient', snapshot.ambient);
      setRadio(root, 'bk-scs4-texture', snapshot.texture);
      setRadio(root, 'bk-scs4-easy-color', snapshot.easyColor || snapshot.color);
      setRadio(root, 'bk-scs4-easy-vibe', snapshot.easyVibe || snapshot.energyWord);
      setRadio(root, 'bk-scs4-easy-world', snapshot.easyWorld || 'midnight');

      const title = setValue('[data-bk-scs4-title-input]', snapshot.title);
      setValue('[data-bk-scs4-layer-instrument]', snapshot.layerInstrument);
      setValue('[data-bk-scs4-harmony]', snapshot.harmony);
      setValue('[data-bk-scs4-layer-level]', snapshot.layerLevel);
      setValue('[data-bk-scs4-tempo]', snapshot.tempo);
      setValue('[data-bk-scs4-energy]', snapshot.energy);
      setValue('[data-bk-scs4-ambient-level]', snapshot.ambientLevel);
      setValue('[data-bk-scs4-reverb]', snapshot.reverb);
      setValue('[data-bk-scs4-echo]', snapshot.echo);
      setValue('[data-bk-scs4-space]', snapshot.space);
      setValue('[data-bk-scs4-phrase]', snapshot.phrase);
      setValue('[data-bk-scs4-voice]', snapshot.voice);
      qa(root, '[data-bk-scs4-master-volume]').forEach((input) => { input.value = String(snapshot.masterVolume); });
      const include = q(root, '[data-bk-scs4-include-phrase]');
      if (include) include.checked = Boolean(snapshot.includePhrase);

      /* One normal input event lets the established Studio engine read the
         complete batch after every field has been restored. */
      title?.dispatchEvent(new Event('input', { bubbles:true }));
      q(root, 'input[name^="bk-scs4-ambient-"]:checked')?.dispatchEvent(new Event('change', { bubbles:true }));
      restoring = false;
      render(snapshot);
      status(message);
    }

    function undo() {
      if (historyIndex <= 0) return;
      historyIndex -= 1;
      applySnapshot(history[historyIndex], 'Undid the last creative choice.');
      updateHistoryButtons();
    }

    function redo() {
      if (historyIndex < 0 || historyIndex >= history.length - 1) return;
      historyIndex += 1;
      applySnapshot(history[historyIndex], 'Restored the next creative choice.');
      updateHistoryButtons();
    }

    function openReset() {
      if (!refs.resetDialog) return;
      if (typeof refs.resetDialog.showModal === 'function') refs.resetDialog.showModal();
      else refs.resetDialog.setAttribute('open', '');
    }

    function confirmReset() {
      pushHistory(captureState());
      const reset = { ...RESET_STATE };
      applySnapshot(reset, 'Creation reset. Undo remains available.');
      pushHistory(reset);
      refs.resetDialog?.close?.();
      updateHistoryButtons();
    }

    function openDrawer() {
      lastFocus = document.activeElement;
      root.classList.add('is-creation-pulse-open');
      refs.drawer.removeAttribute('inert');
      refs.drawer.setAttribute('aria-hidden', 'false');
      refs.toggle.setAttribute('aria-expanded', 'true');
      if (refs.scrim) refs.scrim.hidden = false;
      render();
      startAnimation();
      requestAnimationFrame(() => refs.close?.focus({ preventScroll:true }));
      status('Creation Pulse opened. Your current choices are visible.');
    }

    function closeDrawer() {
      root.classList.remove('is-creation-pulse-open', 'is-creation-pulse-expanded');
      refs.drawer.setAttribute('aria-hidden', 'true');
      refs.drawer.setAttribute('inert', '');
      refs.toggle.setAttribute('aria-expanded', 'false');
      if (refs.scrim) refs.scrim.hidden = true;
      stopAnimation();
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll:true });
      else refs.toggle.focus({ preventScroll:true });
    }

    function toggleDrawer() {
      if (root.classList.contains('is-creation-pulse-open')) closeDrawer();
      else openDrawer();
    }

    function toggleSnap() {
      const expanded = root.classList.toggle('is-creation-pulse-expanded');
      if (refs.snap) {
        refs.snap.setAttribute('aria-expanded', String(expanded));
        refs.snap.setAttribute('aria-label', expanded ? 'Collapse Creation Pulse preview' : 'Expand Creation Pulse preview');
        const label = q(refs.snap, 'em');
        if (label) label.textContent = expanded ? 'Collapse preview' : 'Expand preview';
      }
      requestAnimationFrame(() => render());
    }

    function currentPlayButton() {
      return qa(root, '[data-bk-scs4-play]').find((button) => !button.closest('.bk-scs4__mobile-dock')) || q(root, '[data-bk-scs4-play]');
    }

    function hearPreview() {
      auditionArmed = true;
      if (!root.classList.contains('is-playing')) currentPlayButton()?.click();
      syncTransport();
      status('Preview playing. Change a choice to hear the mix evolve.');
    }

    function stopPreview() {
      const stop = qa(root, '[data-bk-scs4-stop]').find((button) => !button.disabled) || q(root, '[data-bk-scs4-stop]');
      stop?.click();
      syncTransport();
      status('Preview stopped. Your visual remains available.');
    }

    function syncTransport() {
      const playing = root.classList.contains('is-playing');
      if (refs.hear) {
        refs.hear.setAttribute('aria-pressed', String(playing));
        const strong = q(refs.hear, 'strong');
        if (strong) strong.textContent = playing ? 'Preview Playing' : 'Hear Preview';
      }
      if (refs.stop) refs.stop.disabled = !playing;
    }

    function syncVolume(sourceValue) {
      const value = clamp(Number(sourceValue ?? 86), 0, 100);
      if (refs.volume) refs.volume.value = String(value);
      if (refs.volumeOutput) refs.volumeOutput.textContent = `${value}%`;
      const masters = qa(root, '[data-bk-scs4-master-volume]');
      masters.forEach((input) => { input.value = String(value); });
      masters[0]?.dispatchEvent(new Event('input', { bubbles:true }));
    }

    function render(state = captureState()) {
      root.style.setProperty('--bk-scs11-color', state.color);
      refs.drawer.style.setProperty('--bk-scs11-color', state.color);
      if (refs.title) refs.title.textContent = state.title;
      if (refs.phrase) {
        refs.phrase.textContent = state.phrase;
        refs.phrase.hidden = !state.phrase;
      }
      if (refs.volume) refs.volume.value = String(state.masterVolume);
      if (refs.volumeOutput) refs.volumeOutput.textContent = `${state.masterVolume}%`;
      if (refs.chips) {
        const chips = [
          state.colorName,
          titleCase(state.rhythm),
          titleCase(state.instrument),
          state.layerInstrument !== 'none' ? `+ ${titleCase(state.layerInstrument)}` : null,
          titleCase(state.ambient),
          `${state.tempo} BPM`,
          titleCase(state.texture)
        ].filter(Boolean);
        refs.chips.innerHTML = chips.map((chip) => `<span>${String(chip).replace(/[&<>"']/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]))}</span>`).join('');
      }
      draw(state, performance.now());
      syncTransport();
    }

    function resizeCanvas() {
      if (!refs.canvas) return;
      const rect = refs.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));
      if (refs.canvas.width !== width || refs.canvas.height !== height) {
        refs.canvas.width = width;
        refs.canvas.height = height;
      }
    }

    function draw(state, time = 0) {
      if (!refs.canvas) return;
      resizeCanvas();
      const canvas = refs.canvas;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const width = canvas.width;
      const height = canvas.height;
      const reduced = root.classList.contains('is-reduced-motion') || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const pulse = reduced ? 0 : Math.sin(time / 780) * .5 + .5;
      const color = state.color || '#8a65ff';
      const pattern = PATTERNS[state.rhythm] || PATTERNS.heartbeat;
      ctx.clearRect(0, 0, width, height);

      const base = ctx.createLinearGradient(0, 0, width, height);
      base.addColorStop(0, '#050316');
      base.addColorStop(.48, rgba(color, .34));
      base.addColorStop(1, '#031827');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, width, height);

      const glow = ctx.createRadialGradient(width * .56, height * .43, 0, width * .56, height * .43, width * .48);
      glow.addColorStop(0, rgba(color, .42 + pulse * .12));
      glow.addColorStop(.36, 'rgba(69,223,255,.16)');
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);

      ctx.save();
      ctx.globalAlpha = .12;
      ctx.strokeStyle = '#8cecff';
      ctx.lineWidth = Math.max(1, width / 900);
      const step = Math.max(26, width / 15);
      for (let x = 0; x < width; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke(); }
      for (let y = 0; y < height; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }
      ctx.restore();

      ctx.save();
      ctx.translate(0, height * .5);
      ctx.beginPath();
      const points = 180;
      for (let index = 0; index <= points; index += 1) {
        const x = (index / points) * width;
        const patternValue = pattern[Math.floor((index / points) * pattern.length * 4) % pattern.length] || .08;
        const carrier = Math.sin(index * .34 + time / 180) * .55 + Math.sin(index * .11 - time / 260) * .45;
        const envelope = Math.sin(Math.PI * (index / points));
        const amplitude = height * (.035 + state.energy / 100 * .12) * (patternValue + .18) * envelope;
        const y = carrier * amplitude;
        if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      const wave = ctx.createLinearGradient(0, 0, width, 0);
      wave.addColorStop(0, '#ff56b9');
      wave.addColorStop(.48, '#ffffff');
      wave.addColorStop(1, '#43dcff');
      ctx.strokeStyle = wave;
      ctx.lineWidth = Math.max(2, width / 360);
      ctx.shadowColor = rgba(color, .9);
      ctx.shadowBlur = width / 45;
      ctx.stroke();
      ctx.restore();

      const particleCount = reduced ? 16 : 34;
      for (let index = 0; index < particleCount; index += 1) {
        const seed = index * 97.13;
        const x = ((Math.sin(seed) * .5 + .5) * width + (reduced ? 0 : time * (.008 + (index % 4) * .002))) % width;
        const y = (Math.cos(seed * 1.7) * .5 + .5) * height;
        const radius = (1 + (index % 3)) * Math.max(1, width / 900);
        ctx.beginPath();
        ctx.fillStyle = index % 3 === 0 ? '#ffd56f' : index % 2 === 0 ? '#63e6ff' : rgba(color, .9);
        ctx.globalAlpha = .34 + (index % 5) * .11;
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    function startAnimation() {
      stopAnimation();
      const tick = (time) => {
        if (!root.classList.contains('is-creation-pulse-open')) return;
        frame += 1;
        if (frame % 2 === 0 || root.classList.contains('is-playing')) draw(captureState(), time);
        animationFrame = requestAnimationFrame(tick);
      };
      animationFrame = requestAnimationFrame(tick);
    }

    function stopAnimation() {
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    }

    function scheduleHistory(event) {
      if (restoring) return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;
      const isContinuous = target.matches('input[type="range"], input[type="text"], input[type="color"], textarea');
      clearTimeout(historyTimer);
      historyTimer = window.setTimeout(() => {
        pushHistory(captureState(), 'Choice added to your creation history.');
        render();
      }, isContinuous ? 320 : 30);
    }

    function maybeAudition(event) {
      const target = event.target;
      const instrumentChange = target.matches?.('input[name^="bk-scs4-instrument-"], [data-bk-scs4-layer-instrument]');
      if (!instrumentChange || root.classList.contains('is-playing')) return;
      const soundOn = auditionArmed || qa(root, '[data-bk-scs4-sound]').some((button) => button.getAttribute('aria-pressed') === 'true');
      if (!soundOn) return;
      auditionArmed = true;
      window.setTimeout(() => {
        q(root, '[data-bk-scs4-beat-pad="0"]')?.click();
        status(`${titleCase(captureState().instrument)} auditioned.`);
      }, 90);
    }

    function handleResetCapture(event) {
      const button = event.target.closest?.('[data-bk-scs4-reset]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openReset();
    }

    function handleKeys(event) {
      if (event.key === 'Escape' && root.classList.contains('is-creation-pulse-open')) {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement || event.target?.isContentEditable) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && event.shiftKey) { event.preventDefault(); redo(); }
      else if (key === 'z') { event.preventDefault(); undo(); }
      else if (key === 'y') { event.preventDefault(); redo(); }
    }

    qa(root, '[data-bk-scs11-undo]').forEach((button) => bind(button, 'click', undo));
    qa(root, '[data-bk-scs11-redo]').forEach((button) => bind(button, 'click', redo));
    qa(root, '[data-bk-scs11-reset]').forEach((button) => bind(button, 'click', openReset));
    bind(refs.resetConfirm, 'click', confirmReset);
    bind(refs.toggle, 'click', toggleDrawer);
    bind(refs.close, 'click', closeDrawer);
    bind(refs.scrim, 'click', closeDrawer);
    bind(refs.snap, 'click', toggleSnap);
    bind(refs.hear, 'click', hearPreview);
    bind(refs.stop, 'click', stopPreview);
    bind(refs.volume, 'input', (event) => syncVolume(event.target.value));
    bind(root, 'input', scheduleHistory);
    bind(root, 'change', (event) => { scheduleHistory(event); maybeAudition(event); });
    bind(root, 'click', handleResetCapture, true);
    bind(document, 'keydown', handleKeys);
    bind(window, 'resize', () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => render());
    }, { passive:true });

    const classObserver = new MutationObserver(() => syncTransport());
    classObserver.observe(root, { attributes:true, attributeFilter:['class'] });

    window.setTimeout(() => {
      const initial = captureState();
      pushHistory(initial);
      render(initial);
      syncTransport();
    }, 520);

    mounted.set(root, {
      destroy() {
        clearTimeout(historyTimer);
        cancelAnimationFrame(animationFrame);
        cancelAnimationFrame(resizeFrame);
        classObserver.disconnect();
        bound.forEach(([node, event, handler, options]) => node.removeEventListener(event, handler, options));
        closeDrawer();
      }
    });
  }

  function unmount(root) {
    mounted.get(root)?.destroy();
    mounted.delete(root);
  }

  function mountAll(scope = document) {
    if (scope.matches?.('[data-bk-scs4]')) mount(scope);
    qa(scope, '[data-bk-scs4]').forEach(mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll(), { once:true });
  else mountAll();

  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-bk-scs4]') ? event.target : q(event.target, '[data-bk-scs4]');
    if (root) unmount(root);
  });
})();
